'use strict';

var middlewareHostHeader = require('@aws-sdk/middleware-host-header');
var middlewareLogger = require('@aws-sdk/middleware-logger');
var middlewareRecursionDetection = require('@aws-sdk/middleware-recursion-detection');
var middlewareUserAgent = require('@aws-sdk/middleware-user-agent');
var configResolver = require('@smithy/config-resolver');
var core = require('@smithy/core');
var schema = require('@smithy/core/schema');
var middlewareContentLength = require('@smithy/middleware-content-length');
var middlewareEndpoint = require('@smithy/middleware-endpoint');
var middlewareRetry = require('@smithy/middleware-retry');
var smithyClient = require('@smithy/smithy-client');
var httpAuthSchemeProvider = require('./auth/httpAuthSchemeProvider');
var runtimeConfig = require('./runtimeConfig');
var regionConfigResolver = require('@aws-sdk/region-config-resolver');
var protocolHttp = require('@smithy/protocol-http');
var schemas_0 = require('./schemas/schemas_0');
var errors = require('./models/errors');
var IoTServiceException = require('./models/IoTServiceException');

const resolveClientEndpointParameters = (options) => {
    return Object.assign(options, {
        useDualstackEndpoint: options.useDualstackEndpoint ?? false,
        useFipsEndpoint: options.useFipsEndpoint ?? false,
        defaultSigningName: "iot",
    });
};
const commonParams = {
    UseFIPS: { type: "builtInParams", name: "useFipsEndpoint" },
    Endpoint: { type: "builtInParams", name: "endpoint" },
    Region: { type: "builtInParams", name: "region" },
    UseDualStack: { type: "builtInParams", name: "useDualstackEndpoint" },
};

const getHttpAuthExtensionConfiguration = (runtimeConfig) => {
    const _httpAuthSchemes = runtimeConfig.httpAuthSchemes;
    let _httpAuthSchemeProvider = runtimeConfig.httpAuthSchemeProvider;
    let _credentials = runtimeConfig.credentials;
    return {
        setHttpAuthScheme(httpAuthScheme) {
            const index = _httpAuthSchemes.findIndex((scheme) => scheme.schemeId === httpAuthScheme.schemeId);
            if (index === -1) {
                _httpAuthSchemes.push(httpAuthScheme);
            }
            else {
                _httpAuthSchemes.splice(index, 1, httpAuthScheme);
            }
        },
        httpAuthSchemes() {
            return _httpAuthSchemes;
        },
        setHttpAuthSchemeProvider(httpAuthSchemeProvider) {
            _httpAuthSchemeProvider = httpAuthSchemeProvider;
        },
        httpAuthSchemeProvider() {
            return _httpAuthSchemeProvider;
        },
        setCredentials(credentials) {
            _credentials = credentials;
        },
        credentials() {
            return _credentials;
        },
    };
};
const resolveHttpAuthRuntimeConfig = (config) => {
    return {
        httpAuthSchemes: config.httpAuthSchemes(),
        httpAuthSchemeProvider: config.httpAuthSchemeProvider(),
        credentials: config.credentials(),
    };
};

const resolveRuntimeExtensions = (runtimeConfig, extensions) => {
    const extensionConfiguration = Object.assign(regionConfigResolver.getAwsRegionExtensionConfiguration(runtimeConfig), smithyClient.getDefaultExtensionConfiguration(runtimeConfig), protocolHttp.getHttpHandlerExtensionConfiguration(runtimeConfig), getHttpAuthExtensionConfiguration(runtimeConfig));
    extensions.forEach((extension) => extension.configure(extensionConfiguration));
    return Object.assign(runtimeConfig, regionConfigResolver.resolveAwsRegionExtensionConfiguration(extensionConfiguration), smithyClient.resolveDefaultRuntimeConfig(extensionConfiguration), protocolHttp.resolveHttpHandlerRuntimeConfig(extensionConfiguration), resolveHttpAuthRuntimeConfig(extensionConfiguration));
};

class IoTClient extends smithyClient.Client {
    config;
    constructor(...[configuration]) {
        const _config_0 = runtimeConfig.getRuntimeConfig(configuration || {});
        super(_config_0);
        this.initConfig = _config_0;
        const _config_1 = resolveClientEndpointParameters(_config_0);
        const _config_2 = middlewareUserAgent.resolveUserAgentConfig(_config_1);
        const _config_3 = middlewareRetry.resolveRetryConfig(_config_2);
        const _config_4 = configResolver.resolveRegionConfig(_config_3);
        const _config_5 = middlewareHostHeader.resolveHostHeaderConfig(_config_4);
        const _config_6 = middlewareEndpoint.resolveEndpointConfig(_config_5);
        const _config_7 = httpAuthSchemeProvider.resolveHttpAuthSchemeConfig(_config_6);
        const _config_8 = resolveRuntimeExtensions(_config_7, configuration?.extensions || []);
        this.config = _config_8;
        this.middlewareStack.use(schema.getSchemaSerdePlugin(this.config));
        this.middlewareStack.use(middlewareUserAgent.getUserAgentPlugin(this.config));
        this.middlewareStack.use(middlewareRetry.getOmitRetryHeadersPlugin(this.config));
        this.middlewareStack.use(middlewareRetry.getRetryPlugin(this.config));
        this.middlewareStack.use(middlewareContentLength.getContentLengthPlugin(this.config));
        this.middlewareStack.use(middlewareHostHeader.getHostHeaderPlugin(this.config));
        this.middlewareStack.use(middlewareLogger.getLoggerPlugin(this.config));
        this.middlewareStack.use(middlewareRecursionDetection.getRecursionDetectionPlugin(this.config));
        this.middlewareStack.use(core.getHttpAuthSchemeEndpointRuleSetPlugin(this.config, {
            httpAuthSchemeParametersProvider: httpAuthSchemeProvider.defaultIoTHttpAuthSchemeParametersProvider,
            identityProviderConfigProvider: async (config) => new core.DefaultIdentityProviderConfig({
                "aws.auth#sigv4": config.credentials,
            }),
        }));
        this.middlewareStack.use(core.getHttpSigningPlugin(this.config));
    }
    destroy() {
        super.destroy();
    }
}

class AcceptCertificateTransferCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AcceptCertificateTransfer", {})
    .n("IoTClient", "AcceptCertificateTransferCommand")
    .sc(schemas_0.AcceptCertificateTransfer$)
    .build() {
}

class AddThingToBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AddThingToBillingGroup", {})
    .n("IoTClient", "AddThingToBillingGroupCommand")
    .sc(schemas_0.AddThingToBillingGroup$)
    .build() {
}

class AddThingToThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AddThingToThingGroup", {})
    .n("IoTClient", "AddThingToThingGroupCommand")
    .sc(schemas_0.AddThingToThingGroup$)
    .build() {
}

class AssociateSbomWithPackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AssociateSbomWithPackageVersion", {})
    .n("IoTClient", "AssociateSbomWithPackageVersionCommand")
    .sc(schemas_0.AssociateSbomWithPackageVersion$)
    .build() {
}

class AssociateTargetsWithJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AssociateTargetsWithJob", {})
    .n("IoTClient", "AssociateTargetsWithJobCommand")
    .sc(schemas_0.AssociateTargetsWithJob$)
    .build() {
}

class AttachPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AttachPolicy", {})
    .n("IoTClient", "AttachPolicyCommand")
    .sc(schemas_0.AttachPolicy$)
    .build() {
}

class AttachPrincipalPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AttachPrincipalPolicy", {})
    .n("IoTClient", "AttachPrincipalPolicyCommand")
    .sc(schemas_0.AttachPrincipalPolicy$)
    .build() {
}

class AttachSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AttachSecurityProfile", {})
    .n("IoTClient", "AttachSecurityProfileCommand")
    .sc(schemas_0.AttachSecurityProfile$)
    .build() {
}

class AttachThingPrincipalCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "AttachThingPrincipal", {})
    .n("IoTClient", "AttachThingPrincipalCommand")
    .sc(schemas_0.AttachThingPrincipal$)
    .build() {
}

class CancelAuditMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelAuditMitigationActionsTask", {})
    .n("IoTClient", "CancelAuditMitigationActionsTaskCommand")
    .sc(schemas_0.CancelAuditMitigationActionsTask$)
    .build() {
}

class CancelAuditTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelAuditTask", {})
    .n("IoTClient", "CancelAuditTaskCommand")
    .sc(schemas_0.CancelAuditTask$)
    .build() {
}

class CancelCertificateTransferCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelCertificateTransfer", {})
    .n("IoTClient", "CancelCertificateTransferCommand")
    .sc(schemas_0.CancelCertificateTransfer$)
    .build() {
}

class CancelDetectMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelDetectMitigationActionsTask", {})
    .n("IoTClient", "CancelDetectMitigationActionsTaskCommand")
    .sc(schemas_0.CancelDetectMitigationActionsTask$)
    .build() {
}

class CancelJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelJob", {})
    .n("IoTClient", "CancelJobCommand")
    .sc(schemas_0.CancelJob$)
    .build() {
}

class CancelJobExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CancelJobExecution", {})
    .n("IoTClient", "CancelJobExecutionCommand")
    .sc(schemas_0.CancelJobExecution$)
    .build() {
}

class ClearDefaultAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ClearDefaultAuthorizer", {})
    .n("IoTClient", "ClearDefaultAuthorizerCommand")
    .sc(schemas_0.ClearDefaultAuthorizer$)
    .build() {
}

class ConfirmTopicRuleDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ConfirmTopicRuleDestination", {})
    .n("IoTClient", "ConfirmTopicRuleDestinationCommand")
    .sc(schemas_0.ConfirmTopicRuleDestination$)
    .build() {
}

class CreateAuditSuppressionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateAuditSuppression", {})
    .n("IoTClient", "CreateAuditSuppressionCommand")
    .sc(schemas_0.CreateAuditSuppression$)
    .build() {
}

class CreateAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateAuthorizer", {})
    .n("IoTClient", "CreateAuthorizerCommand")
    .sc(schemas_0.CreateAuthorizer$)
    .build() {
}

class CreateBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateBillingGroup", {})
    .n("IoTClient", "CreateBillingGroupCommand")
    .sc(schemas_0.CreateBillingGroup$)
    .build() {
}

class CreateCertificateFromCsrCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateCertificateFromCsr", {})
    .n("IoTClient", "CreateCertificateFromCsrCommand")
    .sc(schemas_0.CreateCertificateFromCsr$)
    .build() {
}

class CreateCertificateProviderCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateCertificateProvider", {})
    .n("IoTClient", "CreateCertificateProviderCommand")
    .sc(schemas_0.CreateCertificateProvider$)
    .build() {
}

class CreateCommandCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateCommand", {})
    .n("IoTClient", "CreateCommandCommand")
    .sc(schemas_0.CreateCommand$)
    .build() {
}

class CreateCustomMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateCustomMetric", {})
    .n("IoTClient", "CreateCustomMetricCommand")
    .sc(schemas_0.CreateCustomMetric$)
    .build() {
}

class CreateDimensionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateDimension", {})
    .n("IoTClient", "CreateDimensionCommand")
    .sc(schemas_0.CreateDimension$)
    .build() {
}

class CreateDomainConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateDomainConfiguration", {})
    .n("IoTClient", "CreateDomainConfigurationCommand")
    .sc(schemas_0.CreateDomainConfiguration$)
    .build() {
}

class CreateDynamicThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateDynamicThingGroup", {})
    .n("IoTClient", "CreateDynamicThingGroupCommand")
    .sc(schemas_0.CreateDynamicThingGroup$)
    .build() {
}

class CreateFleetMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateFleetMetric", {})
    .n("IoTClient", "CreateFleetMetricCommand")
    .sc(schemas_0.CreateFleetMetric$)
    .build() {
}

class CreateJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateJob", {})
    .n("IoTClient", "CreateJobCommand")
    .sc(schemas_0.CreateJob$)
    .build() {
}

class CreateJobTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateJobTemplate", {})
    .n("IoTClient", "CreateJobTemplateCommand")
    .sc(schemas_0.CreateJobTemplate$)
    .build() {
}

class CreateKeysAndCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateKeysAndCertificate", {})
    .n("IoTClient", "CreateKeysAndCertificateCommand")
    .sc(schemas_0.CreateKeysAndCertificate$)
    .build() {
}

class CreateMitigationActionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateMitigationAction", {})
    .n("IoTClient", "CreateMitigationActionCommand")
    .sc(schemas_0.CreateMitigationAction$)
    .build() {
}

class CreateOTAUpdateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateOTAUpdate", {})
    .n("IoTClient", "CreateOTAUpdateCommand")
    .sc(schemas_0.CreateOTAUpdate$)
    .build() {
}

class CreatePackageCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreatePackage", {})
    .n("IoTClient", "CreatePackageCommand")
    .sc(schemas_0.CreatePackage$)
    .build() {
}

class CreatePackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreatePackageVersion", {})
    .n("IoTClient", "CreatePackageVersionCommand")
    .sc(schemas_0.CreatePackageVersion$)
    .build() {
}

class CreatePolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreatePolicy", {})
    .n("IoTClient", "CreatePolicyCommand")
    .sc(schemas_0.CreatePolicy$)
    .build() {
}

class CreatePolicyVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreatePolicyVersion", {})
    .n("IoTClient", "CreatePolicyVersionCommand")
    .sc(schemas_0.CreatePolicyVersion$)
    .build() {
}

class CreateProvisioningClaimCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateProvisioningClaim", {})
    .n("IoTClient", "CreateProvisioningClaimCommand")
    .sc(schemas_0.CreateProvisioningClaim$)
    .build() {
}

class CreateProvisioningTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateProvisioningTemplate", {})
    .n("IoTClient", "CreateProvisioningTemplateCommand")
    .sc(schemas_0.CreateProvisioningTemplate$)
    .build() {
}

class CreateProvisioningTemplateVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateProvisioningTemplateVersion", {})
    .n("IoTClient", "CreateProvisioningTemplateVersionCommand")
    .sc(schemas_0.CreateProvisioningTemplateVersion$)
    .build() {
}

class CreateRoleAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateRoleAlias", {})
    .n("IoTClient", "CreateRoleAliasCommand")
    .sc(schemas_0.CreateRoleAlias$)
    .build() {
}

class CreateScheduledAuditCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateScheduledAudit", {})
    .n("IoTClient", "CreateScheduledAuditCommand")
    .sc(schemas_0.CreateScheduledAudit$)
    .build() {
}

class CreateSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateSecurityProfile", {})
    .n("IoTClient", "CreateSecurityProfileCommand")
    .sc(schemas_0.CreateSecurityProfile$)
    .build() {
}

class CreateStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateStream", {})
    .n("IoTClient", "CreateStreamCommand")
    .sc(schemas_0.CreateStream$)
    .build() {
}

class CreateThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateThing", {})
    .n("IoTClient", "CreateThingCommand")
    .sc(schemas_0.CreateThing$)
    .build() {
}

class CreateThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateThingGroup", {})
    .n("IoTClient", "CreateThingGroupCommand")
    .sc(schemas_0.CreateThingGroup$)
    .build() {
}

class CreateThingTypeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateThingType", {})
    .n("IoTClient", "CreateThingTypeCommand")
    .sc(schemas_0.CreateThingType$)
    .build() {
}

class CreateTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateTopicRule", {})
    .n("IoTClient", "CreateTopicRuleCommand")
    .sc(schemas_0.CreateTopicRule$)
    .build() {
}

class CreateTopicRuleDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "CreateTopicRuleDestination", {})
    .n("IoTClient", "CreateTopicRuleDestinationCommand")
    .sc(schemas_0.CreateTopicRuleDestination$)
    .build() {
}

class DeleteAccountAuditConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteAccountAuditConfiguration", {})
    .n("IoTClient", "DeleteAccountAuditConfigurationCommand")
    .sc(schemas_0.DeleteAccountAuditConfiguration$)
    .build() {
}

class DeleteAuditSuppressionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteAuditSuppression", {})
    .n("IoTClient", "DeleteAuditSuppressionCommand")
    .sc(schemas_0.DeleteAuditSuppression$)
    .build() {
}

class DeleteAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteAuthorizer", {})
    .n("IoTClient", "DeleteAuthorizerCommand")
    .sc(schemas_0.DeleteAuthorizer$)
    .build() {
}

class DeleteBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteBillingGroup", {})
    .n("IoTClient", "DeleteBillingGroupCommand")
    .sc(schemas_0.DeleteBillingGroup$)
    .build() {
}

class DeleteCACertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCACertificate", {})
    .n("IoTClient", "DeleteCACertificateCommand")
    .sc(schemas_0.DeleteCACertificate$)
    .build() {
}

class DeleteCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCertificate", {})
    .n("IoTClient", "DeleteCertificateCommand")
    .sc(schemas_0.DeleteCertificate$)
    .build() {
}

class DeleteCertificateProviderCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCertificateProvider", {})
    .n("IoTClient", "DeleteCertificateProviderCommand")
    .sc(schemas_0.DeleteCertificateProvider$)
    .build() {
}

class DeleteCommandCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCommand", {})
    .n("IoTClient", "DeleteCommandCommand")
    .sc(schemas_0.DeleteCommand$)
    .build() {
}

class DeleteCommandExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCommandExecution", {})
    .n("IoTClient", "DeleteCommandExecutionCommand")
    .sc(schemas_0.DeleteCommandExecution$)
    .build() {
}

class DeleteCustomMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteCustomMetric", {})
    .n("IoTClient", "DeleteCustomMetricCommand")
    .sc(schemas_0.DeleteCustomMetric$)
    .build() {
}

class DeleteDimensionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteDimension", {})
    .n("IoTClient", "DeleteDimensionCommand")
    .sc(schemas_0.DeleteDimension$)
    .build() {
}

class DeleteDomainConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteDomainConfiguration", {})
    .n("IoTClient", "DeleteDomainConfigurationCommand")
    .sc(schemas_0.DeleteDomainConfiguration$)
    .build() {
}

class DeleteDynamicThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteDynamicThingGroup", {})
    .n("IoTClient", "DeleteDynamicThingGroupCommand")
    .sc(schemas_0.DeleteDynamicThingGroup$)
    .build() {
}

class DeleteFleetMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteFleetMetric", {})
    .n("IoTClient", "DeleteFleetMetricCommand")
    .sc(schemas_0.DeleteFleetMetric$)
    .build() {
}

class DeleteJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteJob", {})
    .n("IoTClient", "DeleteJobCommand")
    .sc(schemas_0.DeleteJob$)
    .build() {
}

class DeleteJobExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteJobExecution", {})
    .n("IoTClient", "DeleteJobExecutionCommand")
    .sc(schemas_0.DeleteJobExecution$)
    .build() {
}

class DeleteJobTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteJobTemplate", {})
    .n("IoTClient", "DeleteJobTemplateCommand")
    .sc(schemas_0.DeleteJobTemplate$)
    .build() {
}

class DeleteMitigationActionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteMitigationAction", {})
    .n("IoTClient", "DeleteMitigationActionCommand")
    .sc(schemas_0.DeleteMitigationAction$)
    .build() {
}

class DeleteOTAUpdateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteOTAUpdate", {})
    .n("IoTClient", "DeleteOTAUpdateCommand")
    .sc(schemas_0.DeleteOTAUpdate$)
    .build() {
}

class DeletePackageCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeletePackage", {})
    .n("IoTClient", "DeletePackageCommand")
    .sc(schemas_0.DeletePackage$)
    .build() {
}

class DeletePackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeletePackageVersion", {})
    .n("IoTClient", "DeletePackageVersionCommand")
    .sc(schemas_0.DeletePackageVersion$)
    .build() {
}

class DeletePolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeletePolicy", {})
    .n("IoTClient", "DeletePolicyCommand")
    .sc(schemas_0.DeletePolicy$)
    .build() {
}

class DeletePolicyVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeletePolicyVersion", {})
    .n("IoTClient", "DeletePolicyVersionCommand")
    .sc(schemas_0.DeletePolicyVersion$)
    .build() {
}

class DeleteProvisioningTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteProvisioningTemplate", {})
    .n("IoTClient", "DeleteProvisioningTemplateCommand")
    .sc(schemas_0.DeleteProvisioningTemplate$)
    .build() {
}

class DeleteProvisioningTemplateVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteProvisioningTemplateVersion", {})
    .n("IoTClient", "DeleteProvisioningTemplateVersionCommand")
    .sc(schemas_0.DeleteProvisioningTemplateVersion$)
    .build() {
}

class DeleteRegistrationCodeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteRegistrationCode", {})
    .n("IoTClient", "DeleteRegistrationCodeCommand")
    .sc(schemas_0.DeleteRegistrationCode$)
    .build() {
}

class DeleteRoleAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteRoleAlias", {})
    .n("IoTClient", "DeleteRoleAliasCommand")
    .sc(schemas_0.DeleteRoleAlias$)
    .build() {
}

class DeleteScheduledAuditCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteScheduledAudit", {})
    .n("IoTClient", "DeleteScheduledAuditCommand")
    .sc(schemas_0.DeleteScheduledAudit$)
    .build() {
}

class DeleteSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteSecurityProfile", {})
    .n("IoTClient", "DeleteSecurityProfileCommand")
    .sc(schemas_0.DeleteSecurityProfile$)
    .build() {
}

class DeleteStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteStream", {})
    .n("IoTClient", "DeleteStreamCommand")
    .sc(schemas_0.DeleteStream$)
    .build() {
}

class DeleteThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteThing", {})
    .n("IoTClient", "DeleteThingCommand")
    .sc(schemas_0.DeleteThing$)
    .build() {
}

class DeleteThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteThingGroup", {})
    .n("IoTClient", "DeleteThingGroupCommand")
    .sc(schemas_0.DeleteThingGroup$)
    .build() {
}

class DeleteThingTypeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteThingType", {})
    .n("IoTClient", "DeleteThingTypeCommand")
    .sc(schemas_0.DeleteThingType$)
    .build() {
}

class DeleteTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteTopicRule", {})
    .n("IoTClient", "DeleteTopicRuleCommand")
    .sc(schemas_0.DeleteTopicRule$)
    .build() {
}

class DeleteTopicRuleDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteTopicRuleDestination", {})
    .n("IoTClient", "DeleteTopicRuleDestinationCommand")
    .sc(schemas_0.DeleteTopicRuleDestination$)
    .build() {
}

class DeleteV2LoggingLevelCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeleteV2LoggingLevel", {})
    .n("IoTClient", "DeleteV2LoggingLevelCommand")
    .sc(schemas_0.DeleteV2LoggingLevel$)
    .build() {
}

class DeprecateThingTypeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DeprecateThingType", {})
    .n("IoTClient", "DeprecateThingTypeCommand")
    .sc(schemas_0.DeprecateThingType$)
    .build() {
}

class DescribeAccountAuditConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAccountAuditConfiguration", {})
    .n("IoTClient", "DescribeAccountAuditConfigurationCommand")
    .sc(schemas_0.DescribeAccountAuditConfiguration$)
    .build() {
}

class DescribeAuditFindingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAuditFinding", {})
    .n("IoTClient", "DescribeAuditFindingCommand")
    .sc(schemas_0.DescribeAuditFinding$)
    .build() {
}

class DescribeAuditMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAuditMitigationActionsTask", {})
    .n("IoTClient", "DescribeAuditMitigationActionsTaskCommand")
    .sc(schemas_0.DescribeAuditMitigationActionsTask$)
    .build() {
}

class DescribeAuditSuppressionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAuditSuppression", {})
    .n("IoTClient", "DescribeAuditSuppressionCommand")
    .sc(schemas_0.DescribeAuditSuppression$)
    .build() {
}

class DescribeAuditTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAuditTask", {})
    .n("IoTClient", "DescribeAuditTaskCommand")
    .sc(schemas_0.DescribeAuditTask$)
    .build() {
}

class DescribeAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeAuthorizer", {})
    .n("IoTClient", "DescribeAuthorizerCommand")
    .sc(schemas_0.DescribeAuthorizer$)
    .build() {
}

class DescribeBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeBillingGroup", {})
    .n("IoTClient", "DescribeBillingGroupCommand")
    .sc(schemas_0.DescribeBillingGroup$)
    .build() {
}

class DescribeCACertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeCACertificate", {})
    .n("IoTClient", "DescribeCACertificateCommand")
    .sc(schemas_0.DescribeCACertificate$)
    .build() {
}

class DescribeCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeCertificate", {})
    .n("IoTClient", "DescribeCertificateCommand")
    .sc(schemas_0.DescribeCertificate$)
    .build() {
}

class DescribeCertificateProviderCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeCertificateProvider", {})
    .n("IoTClient", "DescribeCertificateProviderCommand")
    .sc(schemas_0.DescribeCertificateProvider$)
    .build() {
}

class DescribeCustomMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeCustomMetric", {})
    .n("IoTClient", "DescribeCustomMetricCommand")
    .sc(schemas_0.DescribeCustomMetric$)
    .build() {
}

class DescribeDefaultAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeDefaultAuthorizer", {})
    .n("IoTClient", "DescribeDefaultAuthorizerCommand")
    .sc(schemas_0.DescribeDefaultAuthorizer$)
    .build() {
}

class DescribeDetectMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeDetectMitigationActionsTask", {})
    .n("IoTClient", "DescribeDetectMitigationActionsTaskCommand")
    .sc(schemas_0.DescribeDetectMitigationActionsTask$)
    .build() {
}

class DescribeDimensionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeDimension", {})
    .n("IoTClient", "DescribeDimensionCommand")
    .sc(schemas_0.DescribeDimension$)
    .build() {
}

class DescribeDomainConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeDomainConfiguration", {})
    .n("IoTClient", "DescribeDomainConfigurationCommand")
    .sc(schemas_0.DescribeDomainConfiguration$)
    .build() {
}

class DescribeEncryptionConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeEncryptionConfiguration", {})
    .n("IoTClient", "DescribeEncryptionConfigurationCommand")
    .sc(schemas_0.DescribeEncryptionConfiguration$)
    .build() {
}

class DescribeEndpointCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeEndpoint", {})
    .n("IoTClient", "DescribeEndpointCommand")
    .sc(schemas_0.DescribeEndpoint$)
    .build() {
}

class DescribeEventConfigurationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeEventConfigurations", {})
    .n("IoTClient", "DescribeEventConfigurationsCommand")
    .sc(schemas_0.DescribeEventConfigurations$)
    .build() {
}

class DescribeFleetMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeFleetMetric", {})
    .n("IoTClient", "DescribeFleetMetricCommand")
    .sc(schemas_0.DescribeFleetMetric$)
    .build() {
}

class DescribeIndexCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeIndex", {})
    .n("IoTClient", "DescribeIndexCommand")
    .sc(schemas_0.DescribeIndex$)
    .build() {
}

class DescribeJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeJob", {})
    .n("IoTClient", "DescribeJobCommand")
    .sc(schemas_0.DescribeJob$)
    .build() {
}

class DescribeJobExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeJobExecution", {})
    .n("IoTClient", "DescribeJobExecutionCommand")
    .sc(schemas_0.DescribeJobExecution$)
    .build() {
}

class DescribeJobTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeJobTemplate", {})
    .n("IoTClient", "DescribeJobTemplateCommand")
    .sc(schemas_0.DescribeJobTemplate$)
    .build() {
}

class DescribeManagedJobTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeManagedJobTemplate", {})
    .n("IoTClient", "DescribeManagedJobTemplateCommand")
    .sc(schemas_0.DescribeManagedJobTemplate$)
    .build() {
}

class DescribeMitigationActionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeMitigationAction", {})
    .n("IoTClient", "DescribeMitigationActionCommand")
    .sc(schemas_0.DescribeMitigationAction$)
    .build() {
}

class DescribeProvisioningTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeProvisioningTemplate", {})
    .n("IoTClient", "DescribeProvisioningTemplateCommand")
    .sc(schemas_0.DescribeProvisioningTemplate$)
    .build() {
}

class DescribeProvisioningTemplateVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeProvisioningTemplateVersion", {})
    .n("IoTClient", "DescribeProvisioningTemplateVersionCommand")
    .sc(schemas_0.DescribeProvisioningTemplateVersion$)
    .build() {
}

class DescribeRoleAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeRoleAlias", {})
    .n("IoTClient", "DescribeRoleAliasCommand")
    .sc(schemas_0.DescribeRoleAlias$)
    .build() {
}

class DescribeScheduledAuditCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeScheduledAudit", {})
    .n("IoTClient", "DescribeScheduledAuditCommand")
    .sc(schemas_0.DescribeScheduledAudit$)
    .build() {
}

class DescribeSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeSecurityProfile", {})
    .n("IoTClient", "DescribeSecurityProfileCommand")
    .sc(schemas_0.DescribeSecurityProfile$)
    .build() {
}

class DescribeStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeStream", {})
    .n("IoTClient", "DescribeStreamCommand")
    .sc(schemas_0.DescribeStream$)
    .build() {
}

class DescribeThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeThing", {})
    .n("IoTClient", "DescribeThingCommand")
    .sc(schemas_0.DescribeThing$)
    .build() {
}

class DescribeThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeThingGroup", {})
    .n("IoTClient", "DescribeThingGroupCommand")
    .sc(schemas_0.DescribeThingGroup$)
    .build() {
}

class DescribeThingRegistrationTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeThingRegistrationTask", {})
    .n("IoTClient", "DescribeThingRegistrationTaskCommand")
    .sc(schemas_0.DescribeThingRegistrationTask$)
    .build() {
}

class DescribeThingTypeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DescribeThingType", {})
    .n("IoTClient", "DescribeThingTypeCommand")
    .sc(schemas_0.DescribeThingType$)
    .build() {
}

class DetachPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DetachPolicy", {})
    .n("IoTClient", "DetachPolicyCommand")
    .sc(schemas_0.DetachPolicy$)
    .build() {
}

class DetachPrincipalPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DetachPrincipalPolicy", {})
    .n("IoTClient", "DetachPrincipalPolicyCommand")
    .sc(schemas_0.DetachPrincipalPolicy$)
    .build() {
}

class DetachSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DetachSecurityProfile", {})
    .n("IoTClient", "DetachSecurityProfileCommand")
    .sc(schemas_0.DetachSecurityProfile$)
    .build() {
}

class DetachThingPrincipalCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DetachThingPrincipal", {})
    .n("IoTClient", "DetachThingPrincipalCommand")
    .sc(schemas_0.DetachThingPrincipal$)
    .build() {
}

class DisableTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DisableTopicRule", {})
    .n("IoTClient", "DisableTopicRuleCommand")
    .sc(schemas_0.DisableTopicRule$)
    .build() {
}

class DisassociateSbomFromPackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "DisassociateSbomFromPackageVersion", {})
    .n("IoTClient", "DisassociateSbomFromPackageVersionCommand")
    .sc(schemas_0.DisassociateSbomFromPackageVersion$)
    .build() {
}

class EnableTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "EnableTopicRule", {})
    .n("IoTClient", "EnableTopicRuleCommand")
    .sc(schemas_0.EnableTopicRule$)
    .build() {
}

class GetBehaviorModelTrainingSummariesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetBehaviorModelTrainingSummaries", {})
    .n("IoTClient", "GetBehaviorModelTrainingSummariesCommand")
    .sc(schemas_0.GetBehaviorModelTrainingSummaries$)
    .build() {
}

class GetBucketsAggregationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetBucketsAggregation", {})
    .n("IoTClient", "GetBucketsAggregationCommand")
    .sc(schemas_0.GetBucketsAggregation$)
    .build() {
}

class GetCardinalityCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetCardinality", {})
    .n("IoTClient", "GetCardinalityCommand")
    .sc(schemas_0.GetCardinality$)
    .build() {
}

class GetCommandCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetCommand", {})
    .n("IoTClient", "GetCommandCommand")
    .sc(schemas_0.GetCommand$)
    .build() {
}

class GetCommandExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetCommandExecution", {})
    .n("IoTClient", "GetCommandExecutionCommand")
    .sc(schemas_0.GetCommandExecution$)
    .build() {
}

class GetEffectivePoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetEffectivePolicies", {})
    .n("IoTClient", "GetEffectivePoliciesCommand")
    .sc(schemas_0.GetEffectivePolicies$)
    .build() {
}

class GetIndexingConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetIndexingConfiguration", {})
    .n("IoTClient", "GetIndexingConfigurationCommand")
    .sc(schemas_0.GetIndexingConfiguration$)
    .build() {
}

class GetJobDocumentCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetJobDocument", {})
    .n("IoTClient", "GetJobDocumentCommand")
    .sc(schemas_0.GetJobDocument$)
    .build() {
}

class GetLoggingOptionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetLoggingOptions", {})
    .n("IoTClient", "GetLoggingOptionsCommand")
    .sc(schemas_0.GetLoggingOptions$)
    .build() {
}

class GetOTAUpdateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetOTAUpdate", {})
    .n("IoTClient", "GetOTAUpdateCommand")
    .sc(schemas_0.GetOTAUpdate$)
    .build() {
}

class GetPackageCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPackage", {})
    .n("IoTClient", "GetPackageCommand")
    .sc(schemas_0.GetPackage$)
    .build() {
}

class GetPackageConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPackageConfiguration", {})
    .n("IoTClient", "GetPackageConfigurationCommand")
    .sc(schemas_0.GetPackageConfiguration$)
    .build() {
}

class GetPackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPackageVersion", {})
    .n("IoTClient", "GetPackageVersionCommand")
    .sc(schemas_0.GetPackageVersion$)
    .build() {
}

class GetPercentilesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPercentiles", {})
    .n("IoTClient", "GetPercentilesCommand")
    .sc(schemas_0.GetPercentiles$)
    .build() {
}

class GetPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPolicy", {})
    .n("IoTClient", "GetPolicyCommand")
    .sc(schemas_0.GetPolicy$)
    .build() {
}

class GetPolicyVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetPolicyVersion", {})
    .n("IoTClient", "GetPolicyVersionCommand")
    .sc(schemas_0.GetPolicyVersion$)
    .build() {
}

class GetRegistrationCodeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetRegistrationCode", {})
    .n("IoTClient", "GetRegistrationCodeCommand")
    .sc(schemas_0.GetRegistrationCode$)
    .build() {
}

class GetStatisticsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetStatistics", {})
    .n("IoTClient", "GetStatisticsCommand")
    .sc(schemas_0.GetStatistics$)
    .build() {
}

class GetThingConnectivityDataCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetThingConnectivityData", {})
    .n("IoTClient", "GetThingConnectivityDataCommand")
    .sc(schemas_0.GetThingConnectivityData$)
    .build() {
}

class GetTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetTopicRule", {})
    .n("IoTClient", "GetTopicRuleCommand")
    .sc(schemas_0.GetTopicRule$)
    .build() {
}

class GetTopicRuleDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetTopicRuleDestination", {})
    .n("IoTClient", "GetTopicRuleDestinationCommand")
    .sc(schemas_0.GetTopicRuleDestination$)
    .build() {
}

class GetV2LoggingOptionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "GetV2LoggingOptions", {})
    .n("IoTClient", "GetV2LoggingOptionsCommand")
    .sc(schemas_0.GetV2LoggingOptions$)
    .build() {
}

class ListActiveViolationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListActiveViolations", {})
    .n("IoTClient", "ListActiveViolationsCommand")
    .sc(schemas_0.ListActiveViolations$)
    .build() {
}

class ListAttachedPoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAttachedPolicies", {})
    .n("IoTClient", "ListAttachedPoliciesCommand")
    .sc(schemas_0.ListAttachedPolicies$)
    .build() {
}

class ListAuditFindingsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuditFindings", {})
    .n("IoTClient", "ListAuditFindingsCommand")
    .sc(schemas_0.ListAuditFindings$)
    .build() {
}

class ListAuditMitigationActionsExecutionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuditMitigationActionsExecutions", {})
    .n("IoTClient", "ListAuditMitigationActionsExecutionsCommand")
    .sc(schemas_0.ListAuditMitigationActionsExecutions$)
    .build() {
}

class ListAuditMitigationActionsTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuditMitigationActionsTasks", {})
    .n("IoTClient", "ListAuditMitigationActionsTasksCommand")
    .sc(schemas_0.ListAuditMitigationActionsTasks$)
    .build() {
}

class ListAuditSuppressionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuditSuppressions", {})
    .n("IoTClient", "ListAuditSuppressionsCommand")
    .sc(schemas_0.ListAuditSuppressions$)
    .build() {
}

class ListAuditTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuditTasks", {})
    .n("IoTClient", "ListAuditTasksCommand")
    .sc(schemas_0.ListAuditTasks$)
    .build() {
}

class ListAuthorizersCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListAuthorizers", {})
    .n("IoTClient", "ListAuthorizersCommand")
    .sc(schemas_0.ListAuthorizers$)
    .build() {
}

class ListBillingGroupsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListBillingGroups", {})
    .n("IoTClient", "ListBillingGroupsCommand")
    .sc(schemas_0.ListBillingGroups$)
    .build() {
}

class ListCACertificatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCACertificates", {})
    .n("IoTClient", "ListCACertificatesCommand")
    .sc(schemas_0.ListCACertificates$)
    .build() {
}

class ListCertificateProvidersCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCertificateProviders", {})
    .n("IoTClient", "ListCertificateProvidersCommand")
    .sc(schemas_0.ListCertificateProviders$)
    .build() {
}

class ListCertificatesByCACommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCertificatesByCA", {})
    .n("IoTClient", "ListCertificatesByCACommand")
    .sc(schemas_0.ListCertificatesByCA$)
    .build() {
}

class ListCertificatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCertificates", {})
    .n("IoTClient", "ListCertificatesCommand")
    .sc(schemas_0.ListCertificates$)
    .build() {
}

class ListCommandExecutionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCommandExecutions", {})
    .n("IoTClient", "ListCommandExecutionsCommand")
    .sc(schemas_0.ListCommandExecutions$)
    .build() {
}

class ListCommandsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCommands", {})
    .n("IoTClient", "ListCommandsCommand")
    .sc(schemas_0.ListCommands$)
    .build() {
}

class ListCustomMetricsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListCustomMetrics", {})
    .n("IoTClient", "ListCustomMetricsCommand")
    .sc(schemas_0.ListCustomMetrics$)
    .build() {
}

class ListDetectMitigationActionsExecutionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListDetectMitigationActionsExecutions", {})
    .n("IoTClient", "ListDetectMitigationActionsExecutionsCommand")
    .sc(schemas_0.ListDetectMitigationActionsExecutions$)
    .build() {
}

class ListDetectMitigationActionsTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListDetectMitigationActionsTasks", {})
    .n("IoTClient", "ListDetectMitigationActionsTasksCommand")
    .sc(schemas_0.ListDetectMitigationActionsTasks$)
    .build() {
}

class ListDimensionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListDimensions", {})
    .n("IoTClient", "ListDimensionsCommand")
    .sc(schemas_0.ListDimensions$)
    .build() {
}

class ListDomainConfigurationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListDomainConfigurations", {})
    .n("IoTClient", "ListDomainConfigurationsCommand")
    .sc(schemas_0.ListDomainConfigurations$)
    .build() {
}

class ListFleetMetricsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListFleetMetrics", {})
    .n("IoTClient", "ListFleetMetricsCommand")
    .sc(schemas_0.ListFleetMetrics$)
    .build() {
}

class ListIndicesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListIndices", {})
    .n("IoTClient", "ListIndicesCommand")
    .sc(schemas_0.ListIndices$)
    .build() {
}

class ListJobExecutionsForJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListJobExecutionsForJob", {})
    .n("IoTClient", "ListJobExecutionsForJobCommand")
    .sc(schemas_0.ListJobExecutionsForJob$)
    .build() {
}

class ListJobExecutionsForThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListJobExecutionsForThing", {})
    .n("IoTClient", "ListJobExecutionsForThingCommand")
    .sc(schemas_0.ListJobExecutionsForThing$)
    .build() {
}

class ListJobsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListJobs", {})
    .n("IoTClient", "ListJobsCommand")
    .sc(schemas_0.ListJobs$)
    .build() {
}

class ListJobTemplatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListJobTemplates", {})
    .n("IoTClient", "ListJobTemplatesCommand")
    .sc(schemas_0.ListJobTemplates$)
    .build() {
}

class ListManagedJobTemplatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListManagedJobTemplates", {})
    .n("IoTClient", "ListManagedJobTemplatesCommand")
    .sc(schemas_0.ListManagedJobTemplates$)
    .build() {
}

class ListMetricValuesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListMetricValues", {})
    .n("IoTClient", "ListMetricValuesCommand")
    .sc(schemas_0.ListMetricValues$)
    .build() {
}

class ListMitigationActionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListMitigationActions", {})
    .n("IoTClient", "ListMitigationActionsCommand")
    .sc(schemas_0.ListMitigationActions$)
    .build() {
}

class ListOTAUpdatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListOTAUpdates", {})
    .n("IoTClient", "ListOTAUpdatesCommand")
    .sc(schemas_0.ListOTAUpdates$)
    .build() {
}

class ListOutgoingCertificatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListOutgoingCertificates", {})
    .n("IoTClient", "ListOutgoingCertificatesCommand")
    .sc(schemas_0.ListOutgoingCertificates$)
    .build() {
}

class ListPackagesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPackages", {})
    .n("IoTClient", "ListPackagesCommand")
    .sc(schemas_0.ListPackages$)
    .build() {
}

class ListPackageVersionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPackageVersions", {})
    .n("IoTClient", "ListPackageVersionsCommand")
    .sc(schemas_0.ListPackageVersions$)
    .build() {
}

class ListPoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPolicies", {})
    .n("IoTClient", "ListPoliciesCommand")
    .sc(schemas_0.ListPolicies$)
    .build() {
}

class ListPolicyPrincipalsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPolicyPrincipals", {})
    .n("IoTClient", "ListPolicyPrincipalsCommand")
    .sc(schemas_0.ListPolicyPrincipals$)
    .build() {
}

class ListPolicyVersionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPolicyVersions", {})
    .n("IoTClient", "ListPolicyVersionsCommand")
    .sc(schemas_0.ListPolicyVersions$)
    .build() {
}

class ListPrincipalPoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPrincipalPolicies", {})
    .n("IoTClient", "ListPrincipalPoliciesCommand")
    .sc(schemas_0.ListPrincipalPolicies$)
    .build() {
}

class ListPrincipalThingsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPrincipalThings", {})
    .n("IoTClient", "ListPrincipalThingsCommand")
    .sc(schemas_0.ListPrincipalThings$)
    .build() {
}

class ListPrincipalThingsV2Command extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListPrincipalThingsV2", {})
    .n("IoTClient", "ListPrincipalThingsV2Command")
    .sc(schemas_0.ListPrincipalThingsV2$)
    .build() {
}

class ListProvisioningTemplatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListProvisioningTemplates", {})
    .n("IoTClient", "ListProvisioningTemplatesCommand")
    .sc(schemas_0.ListProvisioningTemplates$)
    .build() {
}

class ListProvisioningTemplateVersionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListProvisioningTemplateVersions", {})
    .n("IoTClient", "ListProvisioningTemplateVersionsCommand")
    .sc(schemas_0.ListProvisioningTemplateVersions$)
    .build() {
}

class ListRelatedResourcesForAuditFindingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListRelatedResourcesForAuditFinding", {})
    .n("IoTClient", "ListRelatedResourcesForAuditFindingCommand")
    .sc(schemas_0.ListRelatedResourcesForAuditFinding$)
    .build() {
}

class ListRoleAliasesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListRoleAliases", {})
    .n("IoTClient", "ListRoleAliasesCommand")
    .sc(schemas_0.ListRoleAliases$)
    .build() {
}

class ListSbomValidationResultsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListSbomValidationResults", {})
    .n("IoTClient", "ListSbomValidationResultsCommand")
    .sc(schemas_0.ListSbomValidationResults$)
    .build() {
}

class ListScheduledAuditsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListScheduledAudits", {})
    .n("IoTClient", "ListScheduledAuditsCommand")
    .sc(schemas_0.ListScheduledAudits$)
    .build() {
}

class ListSecurityProfilesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListSecurityProfiles", {})
    .n("IoTClient", "ListSecurityProfilesCommand")
    .sc(schemas_0.ListSecurityProfiles$)
    .build() {
}

class ListSecurityProfilesForTargetCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListSecurityProfilesForTarget", {})
    .n("IoTClient", "ListSecurityProfilesForTargetCommand")
    .sc(schemas_0.ListSecurityProfilesForTarget$)
    .build() {
}

class ListStreamsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListStreams", {})
    .n("IoTClient", "ListStreamsCommand")
    .sc(schemas_0.ListStreams$)
    .build() {
}

class ListTagsForResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListTagsForResource", {})
    .n("IoTClient", "ListTagsForResourceCommand")
    .sc(schemas_0.ListTagsForResource$)
    .build() {
}

class ListTargetsForPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListTargetsForPolicy", {})
    .n("IoTClient", "ListTargetsForPolicyCommand")
    .sc(schemas_0.ListTargetsForPolicy$)
    .build() {
}

class ListTargetsForSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListTargetsForSecurityProfile", {})
    .n("IoTClient", "ListTargetsForSecurityProfileCommand")
    .sc(schemas_0.ListTargetsForSecurityProfile$)
    .build() {
}

class ListThingGroupsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingGroups", {})
    .n("IoTClient", "ListThingGroupsCommand")
    .sc(schemas_0.ListThingGroups$)
    .build() {
}

class ListThingGroupsForThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingGroupsForThing", {})
    .n("IoTClient", "ListThingGroupsForThingCommand")
    .sc(schemas_0.ListThingGroupsForThing$)
    .build() {
}

class ListThingPrincipalsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingPrincipals", {})
    .n("IoTClient", "ListThingPrincipalsCommand")
    .sc(schemas_0.ListThingPrincipals$)
    .build() {
}

class ListThingPrincipalsV2Command extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingPrincipalsV2", {})
    .n("IoTClient", "ListThingPrincipalsV2Command")
    .sc(schemas_0.ListThingPrincipalsV2$)
    .build() {
}

class ListThingRegistrationTaskReportsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingRegistrationTaskReports", {})
    .n("IoTClient", "ListThingRegistrationTaskReportsCommand")
    .sc(schemas_0.ListThingRegistrationTaskReports$)
    .build() {
}

class ListThingRegistrationTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingRegistrationTasks", {})
    .n("IoTClient", "ListThingRegistrationTasksCommand")
    .sc(schemas_0.ListThingRegistrationTasks$)
    .build() {
}

class ListThingsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThings", {})
    .n("IoTClient", "ListThingsCommand")
    .sc(schemas_0.ListThings$)
    .build() {
}

class ListThingsInBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingsInBillingGroup", {})
    .n("IoTClient", "ListThingsInBillingGroupCommand")
    .sc(schemas_0.ListThingsInBillingGroup$)
    .build() {
}

class ListThingsInThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingsInThingGroup", {})
    .n("IoTClient", "ListThingsInThingGroupCommand")
    .sc(schemas_0.ListThingsInThingGroup$)
    .build() {
}

class ListThingTypesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListThingTypes", {})
    .n("IoTClient", "ListThingTypesCommand")
    .sc(schemas_0.ListThingTypes$)
    .build() {
}

class ListTopicRuleDestinationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListTopicRuleDestinations", {})
    .n("IoTClient", "ListTopicRuleDestinationsCommand")
    .sc(schemas_0.ListTopicRuleDestinations$)
    .build() {
}

class ListTopicRulesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListTopicRules", {})
    .n("IoTClient", "ListTopicRulesCommand")
    .sc(schemas_0.ListTopicRules$)
    .build() {
}

class ListV2LoggingLevelsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListV2LoggingLevels", {})
    .n("IoTClient", "ListV2LoggingLevelsCommand")
    .sc(schemas_0.ListV2LoggingLevels$)
    .build() {
}

class ListViolationEventsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ListViolationEvents", {})
    .n("IoTClient", "ListViolationEventsCommand")
    .sc(schemas_0.ListViolationEvents$)
    .build() {
}

class PutVerificationStateOnViolationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "PutVerificationStateOnViolation", {})
    .n("IoTClient", "PutVerificationStateOnViolationCommand")
    .sc(schemas_0.PutVerificationStateOnViolation$)
    .build() {
}

class RegisterCACertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RegisterCACertificate", {})
    .n("IoTClient", "RegisterCACertificateCommand")
    .sc(schemas_0.RegisterCACertificate$)
    .build() {
}

class RegisterCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RegisterCertificate", {})
    .n("IoTClient", "RegisterCertificateCommand")
    .sc(schemas_0.RegisterCertificate$)
    .build() {
}

class RegisterCertificateWithoutCACommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RegisterCertificateWithoutCA", {})
    .n("IoTClient", "RegisterCertificateWithoutCACommand")
    .sc(schemas_0.RegisterCertificateWithoutCA$)
    .build() {
}

class RegisterThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RegisterThing", {})
    .n("IoTClient", "RegisterThingCommand")
    .sc(schemas_0.RegisterThing$)
    .build() {
}

class RejectCertificateTransferCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RejectCertificateTransfer", {})
    .n("IoTClient", "RejectCertificateTransferCommand")
    .sc(schemas_0.RejectCertificateTransfer$)
    .build() {
}

class RemoveThingFromBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RemoveThingFromBillingGroup", {})
    .n("IoTClient", "RemoveThingFromBillingGroupCommand")
    .sc(schemas_0.RemoveThingFromBillingGroup$)
    .build() {
}

class RemoveThingFromThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "RemoveThingFromThingGroup", {})
    .n("IoTClient", "RemoveThingFromThingGroupCommand")
    .sc(schemas_0.RemoveThingFromThingGroup$)
    .build() {
}

class ReplaceTopicRuleCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ReplaceTopicRule", {})
    .n("IoTClient", "ReplaceTopicRuleCommand")
    .sc(schemas_0.ReplaceTopicRule$)
    .build() {
}

class SearchIndexCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SearchIndex", {})
    .n("IoTClient", "SearchIndexCommand")
    .sc(schemas_0.SearchIndex$)
    .build() {
}

class SetDefaultAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SetDefaultAuthorizer", {})
    .n("IoTClient", "SetDefaultAuthorizerCommand")
    .sc(schemas_0.SetDefaultAuthorizer$)
    .build() {
}

class SetDefaultPolicyVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SetDefaultPolicyVersion", {})
    .n("IoTClient", "SetDefaultPolicyVersionCommand")
    .sc(schemas_0.SetDefaultPolicyVersion$)
    .build() {
}

class SetLoggingOptionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SetLoggingOptions", {})
    .n("IoTClient", "SetLoggingOptionsCommand")
    .sc(schemas_0.SetLoggingOptions$)
    .build() {
}

class SetV2LoggingLevelCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SetV2LoggingLevel", {})
    .n("IoTClient", "SetV2LoggingLevelCommand")
    .sc(schemas_0.SetV2LoggingLevel$)
    .build() {
}

class SetV2LoggingOptionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "SetV2LoggingOptions", {})
    .n("IoTClient", "SetV2LoggingOptionsCommand")
    .sc(schemas_0.SetV2LoggingOptions$)
    .build() {
}

class StartAuditMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "StartAuditMitigationActionsTask", {})
    .n("IoTClient", "StartAuditMitigationActionsTaskCommand")
    .sc(schemas_0.StartAuditMitigationActionsTask$)
    .build() {
}

class StartDetectMitigationActionsTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "StartDetectMitigationActionsTask", {})
    .n("IoTClient", "StartDetectMitigationActionsTaskCommand")
    .sc(schemas_0.StartDetectMitigationActionsTask$)
    .build() {
}

class StartOnDemandAuditTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "StartOnDemandAuditTask", {})
    .n("IoTClient", "StartOnDemandAuditTaskCommand")
    .sc(schemas_0.StartOnDemandAuditTask$)
    .build() {
}

class StartThingRegistrationTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "StartThingRegistrationTask", {})
    .n("IoTClient", "StartThingRegistrationTaskCommand")
    .sc(schemas_0.StartThingRegistrationTask$)
    .build() {
}

class StopThingRegistrationTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "StopThingRegistrationTask", {})
    .n("IoTClient", "StopThingRegistrationTaskCommand")
    .sc(schemas_0.StopThingRegistrationTask$)
    .build() {
}

class TagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "TagResource", {})
    .n("IoTClient", "TagResourceCommand")
    .sc(schemas_0.TagResource$)
    .build() {
}

class TestAuthorizationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "TestAuthorization", {})
    .n("IoTClient", "TestAuthorizationCommand")
    .sc(schemas_0.TestAuthorization$)
    .build() {
}

class TestInvokeAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "TestInvokeAuthorizer", {})
    .n("IoTClient", "TestInvokeAuthorizerCommand")
    .sc(schemas_0.TestInvokeAuthorizer$)
    .build() {
}

class TransferCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "TransferCertificate", {})
    .n("IoTClient", "TransferCertificateCommand")
    .sc(schemas_0.TransferCertificate$)
    .build() {
}

class UntagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UntagResource", {})
    .n("IoTClient", "UntagResourceCommand")
    .sc(schemas_0.UntagResource$)
    .build() {
}

class UpdateAccountAuditConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateAccountAuditConfiguration", {})
    .n("IoTClient", "UpdateAccountAuditConfigurationCommand")
    .sc(schemas_0.UpdateAccountAuditConfiguration$)
    .build() {
}

class UpdateAuditSuppressionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateAuditSuppression", {})
    .n("IoTClient", "UpdateAuditSuppressionCommand")
    .sc(schemas_0.UpdateAuditSuppression$)
    .build() {
}

class UpdateAuthorizerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateAuthorizer", {})
    .n("IoTClient", "UpdateAuthorizerCommand")
    .sc(schemas_0.UpdateAuthorizer$)
    .build() {
}

class UpdateBillingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateBillingGroup", {})
    .n("IoTClient", "UpdateBillingGroupCommand")
    .sc(schemas_0.UpdateBillingGroup$)
    .build() {
}

class UpdateCACertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateCACertificate", {})
    .n("IoTClient", "UpdateCACertificateCommand")
    .sc(schemas_0.UpdateCACertificate$)
    .build() {
}

class UpdateCertificateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateCertificate", {})
    .n("IoTClient", "UpdateCertificateCommand")
    .sc(schemas_0.UpdateCertificate$)
    .build() {
}

class UpdateCertificateProviderCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateCertificateProvider", {})
    .n("IoTClient", "UpdateCertificateProviderCommand")
    .sc(schemas_0.UpdateCertificateProvider$)
    .build() {
}

class UpdateCommandCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateCommand", {})
    .n("IoTClient", "UpdateCommandCommand")
    .sc(schemas_0.UpdateCommand$)
    .build() {
}

class UpdateCustomMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateCustomMetric", {})
    .n("IoTClient", "UpdateCustomMetricCommand")
    .sc(schemas_0.UpdateCustomMetric$)
    .build() {
}

class UpdateDimensionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateDimension", {})
    .n("IoTClient", "UpdateDimensionCommand")
    .sc(schemas_0.UpdateDimension$)
    .build() {
}

class UpdateDomainConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateDomainConfiguration", {})
    .n("IoTClient", "UpdateDomainConfigurationCommand")
    .sc(schemas_0.UpdateDomainConfiguration$)
    .build() {
}

class UpdateDynamicThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateDynamicThingGroup", {})
    .n("IoTClient", "UpdateDynamicThingGroupCommand")
    .sc(schemas_0.UpdateDynamicThingGroup$)
    .build() {
}

class UpdateEncryptionConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateEncryptionConfiguration", {})
    .n("IoTClient", "UpdateEncryptionConfigurationCommand")
    .sc(schemas_0.UpdateEncryptionConfiguration$)
    .build() {
}

class UpdateEventConfigurationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateEventConfigurations", {})
    .n("IoTClient", "UpdateEventConfigurationsCommand")
    .sc(schemas_0.UpdateEventConfigurations$)
    .build() {
}

class UpdateFleetMetricCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateFleetMetric", {})
    .n("IoTClient", "UpdateFleetMetricCommand")
    .sc(schemas_0.UpdateFleetMetric$)
    .build() {
}

class UpdateIndexingConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateIndexingConfiguration", {})
    .n("IoTClient", "UpdateIndexingConfigurationCommand")
    .sc(schemas_0.UpdateIndexingConfiguration$)
    .build() {
}

class UpdateJobCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateJob", {})
    .n("IoTClient", "UpdateJobCommand")
    .sc(schemas_0.UpdateJob$)
    .build() {
}

class UpdateMitigationActionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateMitigationAction", {})
    .n("IoTClient", "UpdateMitigationActionCommand")
    .sc(schemas_0.UpdateMitigationAction$)
    .build() {
}

class UpdatePackageCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdatePackage", {})
    .n("IoTClient", "UpdatePackageCommand")
    .sc(schemas_0.UpdatePackage$)
    .build() {
}

class UpdatePackageConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdatePackageConfiguration", {})
    .n("IoTClient", "UpdatePackageConfigurationCommand")
    .sc(schemas_0.UpdatePackageConfiguration$)
    .build() {
}

class UpdatePackageVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdatePackageVersion", {})
    .n("IoTClient", "UpdatePackageVersionCommand")
    .sc(schemas_0.UpdatePackageVersion$)
    .build() {
}

class UpdateProvisioningTemplateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateProvisioningTemplate", {})
    .n("IoTClient", "UpdateProvisioningTemplateCommand")
    .sc(schemas_0.UpdateProvisioningTemplate$)
    .build() {
}

class UpdateRoleAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateRoleAlias", {})
    .n("IoTClient", "UpdateRoleAliasCommand")
    .sc(schemas_0.UpdateRoleAlias$)
    .build() {
}

class UpdateScheduledAuditCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateScheduledAudit", {})
    .n("IoTClient", "UpdateScheduledAuditCommand")
    .sc(schemas_0.UpdateScheduledAudit$)
    .build() {
}

class UpdateSecurityProfileCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateSecurityProfile", {})
    .n("IoTClient", "UpdateSecurityProfileCommand")
    .sc(schemas_0.UpdateSecurityProfile$)
    .build() {
}

class UpdateStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateStream", {})
    .n("IoTClient", "UpdateStreamCommand")
    .sc(schemas_0.UpdateStream$)
    .build() {
}

class UpdateThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateThing", {})
    .n("IoTClient", "UpdateThingCommand")
    .sc(schemas_0.UpdateThing$)
    .build() {
}

class UpdateThingGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateThingGroup", {})
    .n("IoTClient", "UpdateThingGroupCommand")
    .sc(schemas_0.UpdateThingGroup$)
    .build() {
}

class UpdateThingGroupsForThingCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateThingGroupsForThing", {})
    .n("IoTClient", "UpdateThingGroupsForThingCommand")
    .sc(schemas_0.UpdateThingGroupsForThing$)
    .build() {
}

class UpdateThingTypeCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateThingType", {})
    .n("IoTClient", "UpdateThingTypeCommand")
    .sc(schemas_0.UpdateThingType$)
    .build() {
}

class UpdateTopicRuleDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "UpdateTopicRuleDestination", {})
    .n("IoTClient", "UpdateTopicRuleDestinationCommand")
    .sc(schemas_0.UpdateTopicRuleDestination$)
    .build() {
}

class ValidateSecurityProfileBehaviorsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSIotService", "ValidateSecurityProfileBehaviors", {})
    .n("IoTClient", "ValidateSecurityProfileBehaviorsCommand")
    .sc(schemas_0.ValidateSecurityProfileBehaviors$)
    .build() {
}

const paginateGetBehaviorModelTrainingSummaries = core.createPaginator(IoTClient, GetBehaviorModelTrainingSummariesCommand, "nextToken", "nextToken", "maxResults");

const paginateListActiveViolations = core.createPaginator(IoTClient, ListActiveViolationsCommand, "nextToken", "nextToken", "maxResults");

const paginateListAttachedPolicies = core.createPaginator(IoTClient, ListAttachedPoliciesCommand, "marker", "nextMarker", "pageSize");

const paginateListAuditFindings = core.createPaginator(IoTClient, ListAuditFindingsCommand, "nextToken", "nextToken", "maxResults");

const paginateListAuditMitigationActionsExecutions = core.createPaginator(IoTClient, ListAuditMitigationActionsExecutionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListAuditMitigationActionsTasks = core.createPaginator(IoTClient, ListAuditMitigationActionsTasksCommand, "nextToken", "nextToken", "maxResults");

const paginateListAuditSuppressions = core.createPaginator(IoTClient, ListAuditSuppressionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListAuditTasks = core.createPaginator(IoTClient, ListAuditTasksCommand, "nextToken", "nextToken", "maxResults");

const paginateListAuthorizers = core.createPaginator(IoTClient, ListAuthorizersCommand, "marker", "nextMarker", "pageSize");

const paginateListBillingGroups = core.createPaginator(IoTClient, ListBillingGroupsCommand, "nextToken", "nextToken", "maxResults");

const paginateListCACertificates = core.createPaginator(IoTClient, ListCACertificatesCommand, "marker", "nextMarker", "pageSize");

const paginateListCertificatesByCA = core.createPaginator(IoTClient, ListCertificatesByCACommand, "marker", "nextMarker", "pageSize");

const paginateListCertificates = core.createPaginator(IoTClient, ListCertificatesCommand, "marker", "nextMarker", "pageSize");

const paginateListCommandExecutions = core.createPaginator(IoTClient, ListCommandExecutionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListCommands = core.createPaginator(IoTClient, ListCommandsCommand, "nextToken", "nextToken", "maxResults");

const paginateListCustomMetrics = core.createPaginator(IoTClient, ListCustomMetricsCommand, "nextToken", "nextToken", "maxResults");

const paginateListDetectMitigationActionsExecutions = core.createPaginator(IoTClient, ListDetectMitigationActionsExecutionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListDetectMitigationActionsTasks = core.createPaginator(IoTClient, ListDetectMitigationActionsTasksCommand, "nextToken", "nextToken", "maxResults");

const paginateListDimensions = core.createPaginator(IoTClient, ListDimensionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListDomainConfigurations = core.createPaginator(IoTClient, ListDomainConfigurationsCommand, "marker", "nextMarker", "pageSize");

const paginateListFleetMetrics = core.createPaginator(IoTClient, ListFleetMetricsCommand, "nextToken", "nextToken", "maxResults");

const paginateListIndices = core.createPaginator(IoTClient, ListIndicesCommand, "nextToken", "nextToken", "maxResults");

const paginateListJobExecutionsForJob = core.createPaginator(IoTClient, ListJobExecutionsForJobCommand, "nextToken", "nextToken", "maxResults");

const paginateListJobExecutionsForThing = core.createPaginator(IoTClient, ListJobExecutionsForThingCommand, "nextToken", "nextToken", "maxResults");

const paginateListJobs = core.createPaginator(IoTClient, ListJobsCommand, "nextToken", "nextToken", "maxResults");

const paginateListJobTemplates = core.createPaginator(IoTClient, ListJobTemplatesCommand, "nextToken", "nextToken", "maxResults");

const paginateListManagedJobTemplates = core.createPaginator(IoTClient, ListManagedJobTemplatesCommand, "nextToken", "nextToken", "maxResults");

const paginateListMetricValues = core.createPaginator(IoTClient, ListMetricValuesCommand, "nextToken", "nextToken", "maxResults");

const paginateListMitigationActions = core.createPaginator(IoTClient, ListMitigationActionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListOTAUpdates = core.createPaginator(IoTClient, ListOTAUpdatesCommand, "nextToken", "nextToken", "maxResults");

const paginateListOutgoingCertificates = core.createPaginator(IoTClient, ListOutgoingCertificatesCommand, "marker", "nextMarker", "pageSize");

const paginateListPackages = core.createPaginator(IoTClient, ListPackagesCommand, "nextToken", "nextToken", "maxResults");

const paginateListPackageVersions = core.createPaginator(IoTClient, ListPackageVersionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListPolicies = core.createPaginator(IoTClient, ListPoliciesCommand, "marker", "nextMarker", "pageSize");

const paginateListPolicyPrincipals = core.createPaginator(IoTClient, ListPolicyPrincipalsCommand, "marker", "nextMarker", "pageSize");

const paginateListPrincipalPolicies = core.createPaginator(IoTClient, ListPrincipalPoliciesCommand, "marker", "nextMarker", "pageSize");

const paginateListPrincipalThings = core.createPaginator(IoTClient, ListPrincipalThingsCommand, "nextToken", "nextToken", "maxResults");

const paginateListPrincipalThingsV2 = core.createPaginator(IoTClient, ListPrincipalThingsV2Command, "nextToken", "nextToken", "maxResults");

const paginateListProvisioningTemplates = core.createPaginator(IoTClient, ListProvisioningTemplatesCommand, "nextToken", "nextToken", "maxResults");

const paginateListProvisioningTemplateVersions = core.createPaginator(IoTClient, ListProvisioningTemplateVersionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListRelatedResourcesForAuditFinding = core.createPaginator(IoTClient, ListRelatedResourcesForAuditFindingCommand, "nextToken", "nextToken", "maxResults");

const paginateListRoleAliases = core.createPaginator(IoTClient, ListRoleAliasesCommand, "marker", "nextMarker", "pageSize");

const paginateListSbomValidationResults = core.createPaginator(IoTClient, ListSbomValidationResultsCommand, "nextToken", "nextToken", "maxResults");

const paginateListScheduledAudits = core.createPaginator(IoTClient, ListScheduledAuditsCommand, "nextToken", "nextToken", "maxResults");

const paginateListSecurityProfilesForTarget = core.createPaginator(IoTClient, ListSecurityProfilesForTargetCommand, "nextToken", "nextToken", "maxResults");

const paginateListSecurityProfiles = core.createPaginator(IoTClient, ListSecurityProfilesCommand, "nextToken", "nextToken", "maxResults");

const paginateListStreams = core.createPaginator(IoTClient, ListStreamsCommand, "nextToken", "nextToken", "maxResults");

const paginateListTagsForResource = core.createPaginator(IoTClient, ListTagsForResourceCommand, "nextToken", "nextToken", "");

const paginateListTargetsForPolicy = core.createPaginator(IoTClient, ListTargetsForPolicyCommand, "marker", "nextMarker", "pageSize");

const paginateListTargetsForSecurityProfile = core.createPaginator(IoTClient, ListTargetsForSecurityProfileCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingGroupsForThing = core.createPaginator(IoTClient, ListThingGroupsForThingCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingGroups = core.createPaginator(IoTClient, ListThingGroupsCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingPrincipals = core.createPaginator(IoTClient, ListThingPrincipalsCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingPrincipalsV2 = core.createPaginator(IoTClient, ListThingPrincipalsV2Command, "nextToken", "nextToken", "maxResults");

const paginateListThingRegistrationTaskReports = core.createPaginator(IoTClient, ListThingRegistrationTaskReportsCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingRegistrationTasks = core.createPaginator(IoTClient, ListThingRegistrationTasksCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingsInBillingGroup = core.createPaginator(IoTClient, ListThingsInBillingGroupCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingsInThingGroup = core.createPaginator(IoTClient, ListThingsInThingGroupCommand, "nextToken", "nextToken", "maxResults");

const paginateListThings = core.createPaginator(IoTClient, ListThingsCommand, "nextToken", "nextToken", "maxResults");

const paginateListThingTypes = core.createPaginator(IoTClient, ListThingTypesCommand, "nextToken", "nextToken", "maxResults");

const paginateListTopicRuleDestinations = core.createPaginator(IoTClient, ListTopicRuleDestinationsCommand, "nextToken", "nextToken", "maxResults");

const paginateListTopicRules = core.createPaginator(IoTClient, ListTopicRulesCommand, "nextToken", "nextToken", "maxResults");

const paginateListV2LoggingLevels = core.createPaginator(IoTClient, ListV2LoggingLevelsCommand, "nextToken", "nextToken", "maxResults");

const paginateListViolationEvents = core.createPaginator(IoTClient, ListViolationEventsCommand, "nextToken", "nextToken", "maxResults");

const commands = {
    AcceptCertificateTransferCommand,
    AddThingToBillingGroupCommand,
    AddThingToThingGroupCommand,
    AssociateSbomWithPackageVersionCommand,
    AssociateTargetsWithJobCommand,
    AttachPolicyCommand,
    AttachPrincipalPolicyCommand,
    AttachSecurityProfileCommand,
    AttachThingPrincipalCommand,
    CancelAuditMitigationActionsTaskCommand,
    CancelAuditTaskCommand,
    CancelCertificateTransferCommand,
    CancelDetectMitigationActionsTaskCommand,
    CancelJobCommand,
    CancelJobExecutionCommand,
    ClearDefaultAuthorizerCommand,
    ConfirmTopicRuleDestinationCommand,
    CreateAuditSuppressionCommand,
    CreateAuthorizerCommand,
    CreateBillingGroupCommand,
    CreateCertificateFromCsrCommand,
    CreateCertificateProviderCommand,
    CreateCommandCommand,
    CreateCustomMetricCommand,
    CreateDimensionCommand,
    CreateDomainConfigurationCommand,
    CreateDynamicThingGroupCommand,
    CreateFleetMetricCommand,
    CreateJobCommand,
    CreateJobTemplateCommand,
    CreateKeysAndCertificateCommand,
    CreateMitigationActionCommand,
    CreateOTAUpdateCommand,
    CreatePackageCommand,
    CreatePackageVersionCommand,
    CreatePolicyCommand,
    CreatePolicyVersionCommand,
    CreateProvisioningClaimCommand,
    CreateProvisioningTemplateCommand,
    CreateProvisioningTemplateVersionCommand,
    CreateRoleAliasCommand,
    CreateScheduledAuditCommand,
    CreateSecurityProfileCommand,
    CreateStreamCommand,
    CreateThingCommand,
    CreateThingGroupCommand,
    CreateThingTypeCommand,
    CreateTopicRuleCommand,
    CreateTopicRuleDestinationCommand,
    DeleteAccountAuditConfigurationCommand,
    DeleteAuditSuppressionCommand,
    DeleteAuthorizerCommand,
    DeleteBillingGroupCommand,
    DeleteCACertificateCommand,
    DeleteCertificateCommand,
    DeleteCertificateProviderCommand,
    DeleteCommandCommand,
    DeleteCommandExecutionCommand,
    DeleteCustomMetricCommand,
    DeleteDimensionCommand,
    DeleteDomainConfigurationCommand,
    DeleteDynamicThingGroupCommand,
    DeleteFleetMetricCommand,
    DeleteJobCommand,
    DeleteJobExecutionCommand,
    DeleteJobTemplateCommand,
    DeleteMitigationActionCommand,
    DeleteOTAUpdateCommand,
    DeletePackageCommand,
    DeletePackageVersionCommand,
    DeletePolicyCommand,
    DeletePolicyVersionCommand,
    DeleteProvisioningTemplateCommand,
    DeleteProvisioningTemplateVersionCommand,
    DeleteRegistrationCodeCommand,
    DeleteRoleAliasCommand,
    DeleteScheduledAuditCommand,
    DeleteSecurityProfileCommand,
    DeleteStreamCommand,
    DeleteThingCommand,
    DeleteThingGroupCommand,
    DeleteThingTypeCommand,
    DeleteTopicRuleCommand,
    DeleteTopicRuleDestinationCommand,
    DeleteV2LoggingLevelCommand,
    DeprecateThingTypeCommand,
    DescribeAccountAuditConfigurationCommand,
    DescribeAuditFindingCommand,
    DescribeAuditMitigationActionsTaskCommand,
    DescribeAuditSuppressionCommand,
    DescribeAuditTaskCommand,
    DescribeAuthorizerCommand,
    DescribeBillingGroupCommand,
    DescribeCACertificateCommand,
    DescribeCertificateCommand,
    DescribeCertificateProviderCommand,
    DescribeCustomMetricCommand,
    DescribeDefaultAuthorizerCommand,
    DescribeDetectMitigationActionsTaskCommand,
    DescribeDimensionCommand,
    DescribeDomainConfigurationCommand,
    DescribeEncryptionConfigurationCommand,
    DescribeEndpointCommand,
    DescribeEventConfigurationsCommand,
    DescribeFleetMetricCommand,
    DescribeIndexCommand,
    DescribeJobCommand,
    DescribeJobExecutionCommand,
    DescribeJobTemplateCommand,
    DescribeManagedJobTemplateCommand,
    DescribeMitigationActionCommand,
    DescribeProvisioningTemplateCommand,
    DescribeProvisioningTemplateVersionCommand,
    DescribeRoleAliasCommand,
    DescribeScheduledAuditCommand,
    DescribeSecurityProfileCommand,
    DescribeStreamCommand,
    DescribeThingCommand,
    DescribeThingGroupCommand,
    DescribeThingRegistrationTaskCommand,
    DescribeThingTypeCommand,
    DetachPolicyCommand,
    DetachPrincipalPolicyCommand,
    DetachSecurityProfileCommand,
    DetachThingPrincipalCommand,
    DisableTopicRuleCommand,
    DisassociateSbomFromPackageVersionCommand,
    EnableTopicRuleCommand,
    GetBehaviorModelTrainingSummariesCommand,
    GetBucketsAggregationCommand,
    GetCardinalityCommand,
    GetCommandCommand,
    GetCommandExecutionCommand,
    GetEffectivePoliciesCommand,
    GetIndexingConfigurationCommand,
    GetJobDocumentCommand,
    GetLoggingOptionsCommand,
    GetOTAUpdateCommand,
    GetPackageCommand,
    GetPackageConfigurationCommand,
    GetPackageVersionCommand,
    GetPercentilesCommand,
    GetPolicyCommand,
    GetPolicyVersionCommand,
    GetRegistrationCodeCommand,
    GetStatisticsCommand,
    GetThingConnectivityDataCommand,
    GetTopicRuleCommand,
    GetTopicRuleDestinationCommand,
    GetV2LoggingOptionsCommand,
    ListActiveViolationsCommand,
    ListAttachedPoliciesCommand,
    ListAuditFindingsCommand,
    ListAuditMitigationActionsExecutionsCommand,
    ListAuditMitigationActionsTasksCommand,
    ListAuditSuppressionsCommand,
    ListAuditTasksCommand,
    ListAuthorizersCommand,
    ListBillingGroupsCommand,
    ListCACertificatesCommand,
    ListCertificateProvidersCommand,
    ListCertificatesCommand,
    ListCertificatesByCACommand,
    ListCommandExecutionsCommand,
    ListCommandsCommand,
    ListCustomMetricsCommand,
    ListDetectMitigationActionsExecutionsCommand,
    ListDetectMitigationActionsTasksCommand,
    ListDimensionsCommand,
    ListDomainConfigurationsCommand,
    ListFleetMetricsCommand,
    ListIndicesCommand,
    ListJobExecutionsForJobCommand,
    ListJobExecutionsForThingCommand,
    ListJobsCommand,
    ListJobTemplatesCommand,
    ListManagedJobTemplatesCommand,
    ListMetricValuesCommand,
    ListMitigationActionsCommand,
    ListOTAUpdatesCommand,
    ListOutgoingCertificatesCommand,
    ListPackagesCommand,
    ListPackageVersionsCommand,
    ListPoliciesCommand,
    ListPolicyPrincipalsCommand,
    ListPolicyVersionsCommand,
    ListPrincipalPoliciesCommand,
    ListPrincipalThingsCommand,
    ListPrincipalThingsV2Command,
    ListProvisioningTemplatesCommand,
    ListProvisioningTemplateVersionsCommand,
    ListRelatedResourcesForAuditFindingCommand,
    ListRoleAliasesCommand,
    ListSbomValidationResultsCommand,
    ListScheduledAuditsCommand,
    ListSecurityProfilesCommand,
    ListSecurityProfilesForTargetCommand,
    ListStreamsCommand,
    ListTagsForResourceCommand,
    ListTargetsForPolicyCommand,
    ListTargetsForSecurityProfileCommand,
    ListThingGroupsCommand,
    ListThingGroupsForThingCommand,
    ListThingPrincipalsCommand,
    ListThingPrincipalsV2Command,
    ListThingRegistrationTaskReportsCommand,
    ListThingRegistrationTasksCommand,
    ListThingsCommand,
    ListThingsInBillingGroupCommand,
    ListThingsInThingGroupCommand,
    ListThingTypesCommand,
    ListTopicRuleDestinationsCommand,
    ListTopicRulesCommand,
    ListV2LoggingLevelsCommand,
    ListViolationEventsCommand,
    PutVerificationStateOnViolationCommand,
    RegisterCACertificateCommand,
    RegisterCertificateCommand,
    RegisterCertificateWithoutCACommand,
    RegisterThingCommand,
    RejectCertificateTransferCommand,
    RemoveThingFromBillingGroupCommand,
    RemoveThingFromThingGroupCommand,
    ReplaceTopicRuleCommand,
    SearchIndexCommand,
    SetDefaultAuthorizerCommand,
    SetDefaultPolicyVersionCommand,
    SetLoggingOptionsCommand,
    SetV2LoggingLevelCommand,
    SetV2LoggingOptionsCommand,
    StartAuditMitigationActionsTaskCommand,
    StartDetectMitigationActionsTaskCommand,
    StartOnDemandAuditTaskCommand,
    StartThingRegistrationTaskCommand,
    StopThingRegistrationTaskCommand,
    TagResourceCommand,
    TestAuthorizationCommand,
    TestInvokeAuthorizerCommand,
    TransferCertificateCommand,
    UntagResourceCommand,
    UpdateAccountAuditConfigurationCommand,
    UpdateAuditSuppressionCommand,
    UpdateAuthorizerCommand,
    UpdateBillingGroupCommand,
    UpdateCACertificateCommand,
    UpdateCertificateCommand,
    UpdateCertificateProviderCommand,
    UpdateCommandCommand,
    UpdateCustomMetricCommand,
    UpdateDimensionCommand,
    UpdateDomainConfigurationCommand,
    UpdateDynamicThingGroupCommand,
    UpdateEncryptionConfigurationCommand,
    UpdateEventConfigurationsCommand,
    UpdateFleetMetricCommand,
    UpdateIndexingConfigurationCommand,
    UpdateJobCommand,
    UpdateMitigationActionCommand,
    UpdatePackageCommand,
    UpdatePackageConfigurationCommand,
    UpdatePackageVersionCommand,
    UpdateProvisioningTemplateCommand,
    UpdateRoleAliasCommand,
    UpdateScheduledAuditCommand,
    UpdateSecurityProfileCommand,
    UpdateStreamCommand,
    UpdateThingCommand,
    UpdateThingGroupCommand,
    UpdateThingGroupsForThingCommand,
    UpdateThingTypeCommand,
    UpdateTopicRuleDestinationCommand,
    ValidateSecurityProfileBehaviorsCommand,
};
const paginators = {
    paginateGetBehaviorModelTrainingSummaries,
    paginateListActiveViolations,
    paginateListAttachedPolicies,
    paginateListAuditFindings,
    paginateListAuditMitigationActionsExecutions,
    paginateListAuditMitigationActionsTasks,
    paginateListAuditSuppressions,
    paginateListAuditTasks,
    paginateListAuthorizers,
    paginateListBillingGroups,
    paginateListCACertificates,
    paginateListCertificates,
    paginateListCertificatesByCA,
    paginateListCommandExecutions,
    paginateListCommands,
    paginateListCustomMetrics,
    paginateListDetectMitigationActionsExecutions,
    paginateListDetectMitigationActionsTasks,
    paginateListDimensions,
    paginateListDomainConfigurations,
    paginateListFleetMetrics,
    paginateListIndices,
    paginateListJobExecutionsForJob,
    paginateListJobExecutionsForThing,
    paginateListJobs,
    paginateListJobTemplates,
    paginateListManagedJobTemplates,
    paginateListMetricValues,
    paginateListMitigationActions,
    paginateListOTAUpdates,
    paginateListOutgoingCertificates,
    paginateListPackages,
    paginateListPackageVersions,
    paginateListPolicies,
    paginateListPolicyPrincipals,
    paginateListPrincipalPolicies,
    paginateListPrincipalThings,
    paginateListPrincipalThingsV2,
    paginateListProvisioningTemplates,
    paginateListProvisioningTemplateVersions,
    paginateListRelatedResourcesForAuditFinding,
    paginateListRoleAliases,
    paginateListSbomValidationResults,
    paginateListScheduledAudits,
    paginateListSecurityProfiles,
    paginateListSecurityProfilesForTarget,
    paginateListStreams,
    paginateListTagsForResource,
    paginateListTargetsForPolicy,
    paginateListTargetsForSecurityProfile,
    paginateListThingGroups,
    paginateListThingGroupsForThing,
    paginateListThingPrincipals,
    paginateListThingPrincipalsV2,
    paginateListThingRegistrationTaskReports,
    paginateListThingRegistrationTasks,
    paginateListThings,
    paginateListThingsInBillingGroup,
    paginateListThingsInThingGroup,
    paginateListThingTypes,
    paginateListTopicRuleDestinations,
    paginateListTopicRules,
    paginateListV2LoggingLevels,
    paginateListViolationEvents,
};
class IoT extends IoTClient {
}
smithyClient.createAggregatedClient(commands, IoT, { paginators });

const AbortAction = {
    CANCEL: "CANCEL",
};
const JobExecutionFailureType = {
    ALL: "ALL",
    FAILED: "FAILED",
    REJECTED: "REJECTED",
    TIMED_OUT: "TIMED_OUT",
};
const DynamoKeyType = {
    NUMBER: "NUMBER",
    STRING: "STRING",
};
const CannedAccessControlList = {
    AuthenticatedRead: "authenticated-read",
    AwsExecRead: "aws-exec-read",
    BucketOwnerFullControl: "bucket-owner-full-control",
    BucketOwnerRead: "bucket-owner-read",
    LogDeliveryWrite: "log-delivery-write",
    Private: "private",
    PublicRead: "public-read",
    PublicReadWrite: "public-read-write",
};
const MessageFormat = {
    JSON: "JSON",
    RAW: "RAW",
};
const ActionType = {
    CONNECT: "CONNECT",
    PUBLISH: "PUBLISH",
    RECEIVE: "RECEIVE",
    SUBSCRIBE: "SUBSCRIBE",
};
const ComparisonOperator = {
    GREATER_THAN: "greater-than",
    GREATER_THAN_EQUALS: "greater-than-equals",
    IN_CIDR_SET: "in-cidr-set",
    IN_PORT_SET: "in-port-set",
    IN_SET: "in-set",
    LESS_THAN: "less-than",
    LESS_THAN_EQUALS: "less-than-equals",
    NOT_IN_CIDR_SET: "not-in-cidr-set",
    NOT_IN_PORT_SET: "not-in-port-set",
    NOT_IN_SET: "not-in-set",
};
const ConfidenceLevel = {
    HIGH: "HIGH",
    LOW: "LOW",
    MEDIUM: "MEDIUM",
};
const DimensionValueOperator = {
    IN: "IN",
    NOT_IN: "NOT_IN",
};
const VerificationState = {
    BENIGN_POSITIVE: "BENIGN_POSITIVE",
    FALSE_POSITIVE: "FALSE_POSITIVE",
    TRUE_POSITIVE: "TRUE_POSITIVE",
    UNKNOWN: "UNKNOWN",
};
const AggregationTypeName = {
    CARDINALITY: "Cardinality",
    PERCENTILES: "Percentiles",
    STATISTICS: "Statistics",
};
const AlertTargetType = {
    SNS: "SNS",
};
const ApplicationProtocol = {
    DEFAULT: "DEFAULT",
    HTTPS: "HTTPS",
    MQTT_WSS: "MQTT_WSS",
    SECURE_MQTT: "SECURE_MQTT",
};
const SbomValidationStatus = {
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    SUCCEEDED: "SUCCEEDED",
};
const ThingPrincipalType = {
    EXCLUSIVE_THING: "EXCLUSIVE_THING",
    NON_EXCLUSIVE_THING: "NON_EXCLUSIVE_THING",
};
const ConfigName = {
    CERT_AGE_THRESHOLD_IN_DAYS: "CERT_AGE_THRESHOLD_IN_DAYS",
    CERT_EXPIRATION_THRESHOLD_IN_DAYS: "CERT_EXPIRATION_THRESHOLD_IN_DAYS",
};
const AuditCheckRunStatus = {
    CANCELED: "CANCELED",
    COMPLETED_COMPLIANT: "COMPLETED_COMPLIANT",
    COMPLETED_NON_COMPLIANT: "COMPLETED_NON_COMPLIANT",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    WAITING_FOR_DATA_COLLECTION: "WAITING_FOR_DATA_COLLECTION",
};
const ResourceType = {
    ACCOUNT_SETTINGS: "ACCOUNT_SETTINGS",
    CA_CERTIFICATE: "CA_CERTIFICATE",
    CLIENT_ID: "CLIENT_ID",
    COGNITO_IDENTITY_POOL: "COGNITO_IDENTITY_POOL",
    DEVICE_CERTIFICATE: "DEVICE_CERTIFICATE",
    IAM_ROLE: "IAM_ROLE",
    IOT_POLICY: "IOT_POLICY",
    ISSUER_CERTIFICATE: "ISSUER_CERTIFICATE",
    ROLE_ALIAS: "ROLE_ALIAS",
};
const AuditFindingSeverity = {
    CRITICAL: "CRITICAL",
    HIGH: "HIGH",
    LOW: "LOW",
    MEDIUM: "MEDIUM",
};
const AuditFrequency = {
    BIWEEKLY: "BIWEEKLY",
    DAILY: "DAILY",
    MONTHLY: "MONTHLY",
    WEEKLY: "WEEKLY",
};
const AuditMitigationActionsExecutionStatus = {
    CANCELED: "CANCELED",
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    PENDING: "PENDING",
    SKIPPED: "SKIPPED",
};
const AuditMitigationActionsTaskStatus = {
    CANCELED: "CANCELED",
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
};
const AuditNotificationType = {
    SNS: "SNS",
};
const AuditTaskStatus = {
    CANCELED: "CANCELED",
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
};
const AuditTaskType = {
    ON_DEMAND_AUDIT_TASK: "ON_DEMAND_AUDIT_TASK",
    SCHEDULED_AUDIT_TASK: "SCHEDULED_AUDIT_TASK",
};
const AuthDecision = {
    ALLOWED: "ALLOWED",
    EXPLICIT_DENY: "EXPLICIT_DENY",
    IMPLICIT_DENY: "IMPLICIT_DENY",
};
const AuthenticationType = {
    AWS_SIGV4: "AWS_SIGV4",
    AWS_X509: "AWS_X509",
    CUSTOM_AUTH: "CUSTOM_AUTH",
    CUSTOM_AUTH_X509: "CUSTOM_AUTH_X509",
    DEFAULT: "DEFAULT",
};
const AuthorizerStatus = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
};
const AutoRegistrationStatus = {
    DISABLE: "DISABLE",
    ENABLE: "ENABLE",
};
const CertificateProviderOperation = {
    CreateCertificateFromCsr: "CreateCertificateFromCsr",
};
const CommandParameterType = {
    BINARY: "BINARY",
    BOOLEAN: "BOOLEAN",
    DOUBLE: "DOUBLE",
    INTEGER: "INTEGER",
    LONG: "LONG",
    STRING: "STRING",
    UNSIGNEDLONG: "UNSIGNEDLONG",
};
const CommandParameterValueComparisonOperator = {
    EQUALS: "EQUALS",
    GREATER_THAN: "GREATER_THAN",
    GREATER_THAN_EQUALS: "GREATER_THAN_EQUALS",
    IN_RANGE: "IN_RANGE",
    IN_SET: "IN_SET",
    LESS_THAN: "LESS_THAN",
    LESS_THAN_EQUALS: "LESS_THAN_EQUALS",
    NOT_EQUALS: "NOT_EQUALS",
    NOT_IN_RANGE: "NOT_IN_RANGE",
    NOT_IN_SET: "NOT_IN_SET",
};
const CommandNamespace = {
    AWSIoT: "AWS-IoT",
    AWSIoTFleetWise: "AWS-IoT-FleetWise",
};
const OutputFormat = {
    CBOR: "CBOR",
    JSON: "JSON",
};
const CustomMetricType = {
    IP_ADDRESS_LIST: "ip-address-list",
    NUMBER: "number",
    NUMBER_LIST: "number-list",
    STRING_LIST: "string-list",
};
const DimensionType = {
    TOPIC_FILTER: "TOPIC_FILTER",
};
const ServiceType = {
    CREDENTIAL_PROVIDER: "CREDENTIAL_PROVIDER",
    DATA: "DATA",
    JOBS: "JOBS",
};
const FleetMetricUnit = {
    Bits: "Bits",
    BitsSecond: "Bits/Second",
    Bytes: "Bytes",
    BytesSecond: "Bytes/Second",
    Count: "Count",
    CountSecond: "Count/Second",
    Gigabits: "Gigabits",
    GigabitsSecond: "Gigabits/Second",
    Gigabytes: "Gigabytes",
    GigabytesSecond: "Gigabytes/Second",
    Kilobits: "Kilobits",
    KilobitsSecond: "Kilobits/Second",
    Kilobytes: "Kilobytes",
    KilobytesSecond: "Kilobytes/Second",
    Megabits: "Megabits",
    MegabitsSecond: "Megabits/Second",
    Megabytes: "Megabytes",
    MegabytesSecond: "Megabytes/Second",
    Microseconds: "Microseconds",
    Milliseconds: "Milliseconds",
    None: "None",
    Percent: "Percent",
    Seconds: "Seconds",
    Terabits: "Terabits",
    TerabitsSecond: "Terabits/Second",
    Terabytes: "Terabytes",
    TerabytesSecond: "Terabytes/Second",
};
const RetryableFailureType = {
    ALL: "ALL",
    FAILED: "FAILED",
    TIMED_OUT: "TIMED_OUT",
};
const JobEndBehavior = {
    CANCEL: "CANCEL",
    FORCE_CANCEL: "FORCE_CANCEL",
    STOP_ROLLOUT: "STOP_ROLLOUT",
};
const TargetSelection = {
    CONTINUOUS: "CONTINUOUS",
    SNAPSHOT: "SNAPSHOT",
};
const LogLevel = {
    DEBUG: "DEBUG",
    DISABLED: "DISABLED",
    ERROR: "ERROR",
    INFO: "INFO",
    WARN: "WARN",
};
const PolicyTemplateName = {
    BLANK_POLICY: "BLANK_POLICY",
};
const CACertificateUpdateAction = {
    DEACTIVATE: "DEACTIVATE",
};
const DeviceCertificateUpdateAction = {
    DEACTIVATE: "DEACTIVATE",
};
const AwsJobAbortCriteriaAbortAction = {
    CANCEL: "CANCEL",
};
const AwsJobAbortCriteriaFailureType = {
    ALL: "ALL",
    FAILED: "FAILED",
    REJECTED: "REJECTED",
    TIMED_OUT: "TIMED_OUT",
};
const Protocol = {
    HTTP: "HTTP",
    MQTT: "MQTT",
};
const OTAUpdateStatus = {
    CREATE_COMPLETE: "CREATE_COMPLETE",
    CREATE_FAILED: "CREATE_FAILED",
    CREATE_IN_PROGRESS: "CREATE_IN_PROGRESS",
    CREATE_PENDING: "CREATE_PENDING",
    DELETE_FAILED: "DELETE_FAILED",
    DELETE_IN_PROGRESS: "DELETE_IN_PROGRESS",
};
const PackageVersionStatus = {
    DEPRECATED: "DEPRECATED",
    DRAFT: "DRAFT",
    PUBLISHED: "PUBLISHED",
};
const TemplateType = {
    FLEET_PROVISIONING: "FLEET_PROVISIONING",
    JITP: "JITP",
};
const DayOfWeek = {
    FRI: "FRI",
    MON: "MON",
    SAT: "SAT",
    SUN: "SUN",
    THU: "THU",
    TUE: "TUE",
    WED: "WED",
};
const TopicRuleDestinationStatus = {
    DELETING: "DELETING",
    DISABLED: "DISABLED",
    ENABLED: "ENABLED",
    ERROR: "ERROR",
    IN_PROGRESS: "IN_PROGRESS",
};
const LogTargetType = {
    CLIENT_ID: "CLIENT_ID",
    DEFAULT: "DEFAULT",
    PRINCIPAL_ID: "PRINCIPAL_ID",
    SOURCE_IP: "SOURCE_IP",
    THING_GROUP: "THING_GROUP",
};
const CertificateMode = {
    DEFAULT: "DEFAULT",
    SNI_ONLY: "SNI_ONLY",
};
const CACertificateStatus = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
};
const CertificateStatus = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
    PENDING_ACTIVATION: "PENDING_ACTIVATION",
    PENDING_TRANSFER: "PENDING_TRANSFER",
    REGISTER_INACTIVE: "REGISTER_INACTIVE",
    REVOKED: "REVOKED",
};
const DetectMitigationActionsTaskStatus = {
    CANCELED: "CANCELED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    SUCCESSFUL: "SUCCESSFUL",
};
const DomainConfigurationStatus = {
    DISABLED: "DISABLED",
    ENABLED: "ENABLED",
};
const DomainType = {
    AWS_MANAGED: "AWS_MANAGED",
    CUSTOMER_MANAGED: "CUSTOMER_MANAGED",
    ENDPOINT: "ENDPOINT",
};
const ServerCertificateStatus = {
    INVALID: "INVALID",
    VALID: "VALID",
};
const ConfigurationStatus = {
    HEALTHY: "HEALTHY",
    UNHEALTHY: "UNHEALTHY",
};
const EncryptionType = {
    AWS_OWNED_KMS_KEY: "AWS_OWNED_KMS_KEY",
    CUSTOMER_MANAGED_KMS_KEY: "CUSTOMER_MANAGED_KMS_KEY",
};
const EventType = {
    CA_CERTIFICATE: "CA_CERTIFICATE",
    CERTIFICATE: "CERTIFICATE",
    JOB: "JOB",
    JOB_EXECUTION: "JOB_EXECUTION",
    POLICY: "POLICY",
    THING: "THING",
    THING_GROUP: "THING_GROUP",
    THING_GROUP_HIERARCHY: "THING_GROUP_HIERARCHY",
    THING_GROUP_MEMBERSHIP: "THING_GROUP_MEMBERSHIP",
    THING_TYPE: "THING_TYPE",
    THING_TYPE_ASSOCIATION: "THING_TYPE_ASSOCIATION",
};
const IndexStatus = {
    ACTIVE: "ACTIVE",
    BUILDING: "BUILDING",
    REBUILDING: "REBUILDING",
};
const JobStatus = {
    CANCELED: "CANCELED",
    COMPLETED: "COMPLETED",
    DELETION_IN_PROGRESS: "DELETION_IN_PROGRESS",
    IN_PROGRESS: "IN_PROGRESS",
    SCHEDULED: "SCHEDULED",
};
const JobExecutionStatus = {
    CANCELED: "CANCELED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    QUEUED: "QUEUED",
    REJECTED: "REJECTED",
    REMOVED: "REMOVED",
    SUCCEEDED: "SUCCEEDED",
    TIMED_OUT: "TIMED_OUT",
};
const MitigationActionType = {
    ADD_THINGS_TO_THING_GROUP: "ADD_THINGS_TO_THING_GROUP",
    ENABLE_IOT_LOGGING: "ENABLE_IOT_LOGGING",
    PUBLISH_FINDING_TO_SNS: "PUBLISH_FINDING_TO_SNS",
    REPLACE_DEFAULT_POLICY_VERSION: "REPLACE_DEFAULT_POLICY_VERSION",
    UPDATE_CA_CERTIFICATE: "UPDATE_CA_CERTIFICATE",
    UPDATE_DEVICE_CERTIFICATE: "UPDATE_DEVICE_CERTIFICATE",
};
const DynamicGroupStatus = {
    ACTIVE: "ACTIVE",
    BUILDING: "BUILDING",
    REBUILDING: "REBUILDING",
};
const Status = {
    Cancelled: "Cancelled",
    Cancelling: "Cancelling",
    Completed: "Completed",
    Failed: "Failed",
    InProgress: "InProgress",
};
const ModelStatus = {
    ACTIVE: "ACTIVE",
    EXPIRED: "EXPIRED",
    PENDING_BUILD: "PENDING_BUILD",
};
const CommandExecutionStatus = {
    CREATED: "CREATED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    REJECTED: "REJECTED",
    SUCCEEDED: "SUCCEEDED",
    TIMED_OUT: "TIMED_OUT",
};
const FieldType = {
    BOOLEAN: "Boolean",
    NUMBER: "Number",
    STRING: "String",
};
const ThingGroupIndexingMode = {
    OFF: "OFF",
    ON: "ON",
};
const DeviceDefenderIndexingMode = {
    OFF: "OFF",
    VIOLATIONS: "VIOLATIONS",
};
const TargetFieldOrder = {
    LatLon: "LatLon",
    LonLat: "LonLat",
};
const NamedShadowIndexingMode = {
    OFF: "OFF",
    ON: "ON",
};
const ThingConnectivityIndexingMode = {
    OFF: "OFF",
    STATUS: "STATUS",
};
const ThingIndexingMode = {
    OFF: "OFF",
    REGISTRY: "REGISTRY",
    REGISTRY_AND_SHADOW: "REGISTRY_AND_SHADOW",
};
const DisconnectReasonValue = {
    AUTH_ERROR: "AUTH_ERROR",
    CLIENT_ERROR: "CLIENT_ERROR",
    CLIENT_INITIATED_DISCONNECT: "CLIENT_INITIATED_DISCONNECT",
    CONNECTION_LOST: "CONNECTION_LOST",
    CUSTOMAUTH_TTL_EXPIRATION: "CUSTOMAUTH_TTL_EXPIRATION",
    DUPLICATE_CLIENTID: "DUPLICATE_CLIENTID",
    FORBIDDEN_ACCESS: "FORBIDDEN_ACCESS",
    MQTT_KEEP_ALIVE_TIMEOUT: "MQTT_KEEP_ALIVE_TIMEOUT",
    NONE: "NONE",
    SERVER_ERROR: "SERVER_ERROR",
    SERVER_INITIATED_DISCONNECT: "SERVER_INITIATED_DISCONNECT",
    THROTTLED: "THROTTLED",
    UNKNOWN: "UNKNOWN",
    WEBSOCKET_TTL_EXPIRATION: "WEBSOCKET_TTL_EXPIRATION",
};
const BehaviorCriteriaType = {
    MACHINE_LEARNING: "MACHINE_LEARNING",
    STATIC: "STATIC",
    STATISTICAL: "STATISTICAL",
};
const SortOrder = {
    Ascending: "ASCENDING",
    Descending: "DESCENDING",
};
const DetectMitigationActionExecutionStatus = {
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    SKIPPED: "SKIPPED",
    SUCCESSFUL: "SUCCESSFUL",
};
const SbomValidationResult = {
    FAILED: "FAILED",
    SUCCEEDED: "SUCCEEDED",
};
const SbomValidationErrorCode = {
    FILE_SIZE_LIMIT_EXCEEDED: "FILE_SIZE_LIMIT_EXCEEDED",
    INCOMPATIBLE_FORMAT: "INCOMPATIBLE_FORMAT",
};
const ReportType = {
    ERRORS: "ERRORS",
    RESULTS: "RESULTS",
};
const ViolationEventType = {
    ALARM_CLEARED: "alarm-cleared",
    ALARM_INVALIDATED: "alarm-invalidated",
    IN_ALARM: "in-alarm",
};
const PackageVersionAction = {
    DEPRECATE: "DEPRECATE",
    PUBLISH: "PUBLISH",
};

exports.$Command = smithyClient.Command;
exports.__Client = smithyClient.Client;
exports.IoTServiceException = IoTServiceException.IoTServiceException;
exports.AbortAction = AbortAction;
exports.AcceptCertificateTransferCommand = AcceptCertificateTransferCommand;
exports.ActionType = ActionType;
exports.AddThingToBillingGroupCommand = AddThingToBillingGroupCommand;
exports.AddThingToThingGroupCommand = AddThingToThingGroupCommand;
exports.AggregationTypeName = AggregationTypeName;
exports.AlertTargetType = AlertTargetType;
exports.ApplicationProtocol = ApplicationProtocol;
exports.AssociateSbomWithPackageVersionCommand = AssociateSbomWithPackageVersionCommand;
exports.AssociateTargetsWithJobCommand = AssociateTargetsWithJobCommand;
exports.AttachPolicyCommand = AttachPolicyCommand;
exports.AttachPrincipalPolicyCommand = AttachPrincipalPolicyCommand;
exports.AttachSecurityProfileCommand = AttachSecurityProfileCommand;
exports.AttachThingPrincipalCommand = AttachThingPrincipalCommand;
exports.AuditCheckRunStatus = AuditCheckRunStatus;
exports.AuditFindingSeverity = AuditFindingSeverity;
exports.AuditFrequency = AuditFrequency;
exports.AuditMitigationActionsExecutionStatus = AuditMitigationActionsExecutionStatus;
exports.AuditMitigationActionsTaskStatus = AuditMitigationActionsTaskStatus;
exports.AuditNotificationType = AuditNotificationType;
exports.AuditTaskStatus = AuditTaskStatus;
exports.AuditTaskType = AuditTaskType;
exports.AuthDecision = AuthDecision;
exports.AuthenticationType = AuthenticationType;
exports.AuthorizerStatus = AuthorizerStatus;
exports.AutoRegistrationStatus = AutoRegistrationStatus;
exports.AwsJobAbortCriteriaAbortAction = AwsJobAbortCriteriaAbortAction;
exports.AwsJobAbortCriteriaFailureType = AwsJobAbortCriteriaFailureType;
exports.BehaviorCriteriaType = BehaviorCriteriaType;
exports.CACertificateStatus = CACertificateStatus;
exports.CACertificateUpdateAction = CACertificateUpdateAction;
exports.CancelAuditMitigationActionsTaskCommand = CancelAuditMitigationActionsTaskCommand;
exports.CancelAuditTaskCommand = CancelAuditTaskCommand;
exports.CancelCertificateTransferCommand = CancelCertificateTransferCommand;
exports.CancelDetectMitigationActionsTaskCommand = CancelDetectMitigationActionsTaskCommand;
exports.CancelJobCommand = CancelJobCommand;
exports.CancelJobExecutionCommand = CancelJobExecutionCommand;
exports.CannedAccessControlList = CannedAccessControlList;
exports.CertificateMode = CertificateMode;
exports.CertificateProviderOperation = CertificateProviderOperation;
exports.CertificateStatus = CertificateStatus;
exports.ClearDefaultAuthorizerCommand = ClearDefaultAuthorizerCommand;
exports.CommandExecutionStatus = CommandExecutionStatus;
exports.CommandNamespace = CommandNamespace;
exports.CommandParameterType = CommandParameterType;
exports.CommandParameterValueComparisonOperator = CommandParameterValueComparisonOperator;
exports.ComparisonOperator = ComparisonOperator;
exports.ConfidenceLevel = ConfidenceLevel;
exports.ConfigName = ConfigName;
exports.ConfigurationStatus = ConfigurationStatus;
exports.ConfirmTopicRuleDestinationCommand = ConfirmTopicRuleDestinationCommand;
exports.CreateAuditSuppressionCommand = CreateAuditSuppressionCommand;
exports.CreateAuthorizerCommand = CreateAuthorizerCommand;
exports.CreateBillingGroupCommand = CreateBillingGroupCommand;
exports.CreateCertificateFromCsrCommand = CreateCertificateFromCsrCommand;
exports.CreateCertificateProviderCommand = CreateCertificateProviderCommand;
exports.CreateCommandCommand = CreateCommandCommand;
exports.CreateCustomMetricCommand = CreateCustomMetricCommand;
exports.CreateDimensionCommand = CreateDimensionCommand;
exports.CreateDomainConfigurationCommand = CreateDomainConfigurationCommand;
exports.CreateDynamicThingGroupCommand = CreateDynamicThingGroupCommand;
exports.CreateFleetMetricCommand = CreateFleetMetricCommand;
exports.CreateJobCommand = CreateJobCommand;
exports.CreateJobTemplateCommand = CreateJobTemplateCommand;
exports.CreateKeysAndCertificateCommand = CreateKeysAndCertificateCommand;
exports.CreateMitigationActionCommand = CreateMitigationActionCommand;
exports.CreateOTAUpdateCommand = CreateOTAUpdateCommand;
exports.CreatePackageCommand = CreatePackageCommand;
exports.CreatePackageVersionCommand = CreatePackageVersionCommand;
exports.CreatePolicyCommand = CreatePolicyCommand;
exports.CreatePolicyVersionCommand = CreatePolicyVersionCommand;
exports.CreateProvisioningClaimCommand = CreateProvisioningClaimCommand;
exports.CreateProvisioningTemplateCommand = CreateProvisioningTemplateCommand;
exports.CreateProvisioningTemplateVersionCommand = CreateProvisioningTemplateVersionCommand;
exports.CreateRoleAliasCommand = CreateRoleAliasCommand;
exports.CreateScheduledAuditCommand = CreateScheduledAuditCommand;
exports.CreateSecurityProfileCommand = CreateSecurityProfileCommand;
exports.CreateStreamCommand = CreateStreamCommand;
exports.CreateThingCommand = CreateThingCommand;
exports.CreateThingGroupCommand = CreateThingGroupCommand;
exports.CreateThingTypeCommand = CreateThingTypeCommand;
exports.CreateTopicRuleCommand = CreateTopicRuleCommand;
exports.CreateTopicRuleDestinationCommand = CreateTopicRuleDestinationCommand;
exports.CustomMetricType = CustomMetricType;
exports.DayOfWeek = DayOfWeek;
exports.DeleteAccountAuditConfigurationCommand = DeleteAccountAuditConfigurationCommand;
exports.DeleteAuditSuppressionCommand = DeleteAuditSuppressionCommand;
exports.DeleteAuthorizerCommand = DeleteAuthorizerCommand;
exports.DeleteBillingGroupCommand = DeleteBillingGroupCommand;
exports.DeleteCACertificateCommand = DeleteCACertificateCommand;
exports.DeleteCertificateCommand = DeleteCertificateCommand;
exports.DeleteCertificateProviderCommand = DeleteCertificateProviderCommand;
exports.DeleteCommandCommand = DeleteCommandCommand;
exports.DeleteCommandExecutionCommand = DeleteCommandExecutionCommand;
exports.DeleteCustomMetricCommand = DeleteCustomMetricCommand;
exports.DeleteDimensionCommand = DeleteDimensionCommand;
exports.DeleteDomainConfigurationCommand = DeleteDomainConfigurationCommand;
exports.DeleteDynamicThingGroupCommand = DeleteDynamicThingGroupCommand;
exports.DeleteFleetMetricCommand = DeleteFleetMetricCommand;
exports.DeleteJobCommand = DeleteJobCommand;
exports.DeleteJobExecutionCommand = DeleteJobExecutionCommand;
exports.DeleteJobTemplateCommand = DeleteJobTemplateCommand;
exports.DeleteMitigationActionCommand = DeleteMitigationActionCommand;
exports.DeleteOTAUpdateCommand = DeleteOTAUpdateCommand;
exports.DeletePackageCommand = DeletePackageCommand;
exports.DeletePackageVersionCommand = DeletePackageVersionCommand;
exports.DeletePolicyCommand = DeletePolicyCommand;
exports.DeletePolicyVersionCommand = DeletePolicyVersionCommand;
exports.DeleteProvisioningTemplateCommand = DeleteProvisioningTemplateCommand;
exports.DeleteProvisioningTemplateVersionCommand = DeleteProvisioningTemplateVersionCommand;
exports.DeleteRegistrationCodeCommand = DeleteRegistrationCodeCommand;
exports.DeleteRoleAliasCommand = DeleteRoleAliasCommand;
exports.DeleteScheduledAuditCommand = DeleteScheduledAuditCommand;
exports.DeleteSecurityProfileCommand = DeleteSecurityProfileCommand;
exports.DeleteStreamCommand = DeleteStreamCommand;
exports.DeleteThingCommand = DeleteThingCommand;
exports.DeleteThingGroupCommand = DeleteThingGroupCommand;
exports.DeleteThingTypeCommand = DeleteThingTypeCommand;
exports.DeleteTopicRuleCommand = DeleteTopicRuleCommand;
exports.DeleteTopicRuleDestinationCommand = DeleteTopicRuleDestinationCommand;
exports.DeleteV2LoggingLevelCommand = DeleteV2LoggingLevelCommand;
exports.DeprecateThingTypeCommand = DeprecateThingTypeCommand;
exports.DescribeAccountAuditConfigurationCommand = DescribeAccountAuditConfigurationCommand;
exports.DescribeAuditFindingCommand = DescribeAuditFindingCommand;
exports.DescribeAuditMitigationActionsTaskCommand = DescribeAuditMitigationActionsTaskCommand;
exports.DescribeAuditSuppressionCommand = DescribeAuditSuppressionCommand;
exports.DescribeAuditTaskCommand = DescribeAuditTaskCommand;
exports.DescribeAuthorizerCommand = DescribeAuthorizerCommand;
exports.DescribeBillingGroupCommand = DescribeBillingGroupCommand;
exports.DescribeCACertificateCommand = DescribeCACertificateCommand;
exports.DescribeCertificateCommand = DescribeCertificateCommand;
exports.DescribeCertificateProviderCommand = DescribeCertificateProviderCommand;
exports.DescribeCustomMetricCommand = DescribeCustomMetricCommand;
exports.DescribeDefaultAuthorizerCommand = DescribeDefaultAuthorizerCommand;
exports.DescribeDetectMitigationActionsTaskCommand = DescribeDetectMitigationActionsTaskCommand;
exports.DescribeDimensionCommand = DescribeDimensionCommand;
exports.DescribeDomainConfigurationCommand = DescribeDomainConfigurationCommand;
exports.DescribeEncryptionConfigurationCommand = DescribeEncryptionConfigurationCommand;
exports.DescribeEndpointCommand = DescribeEndpointCommand;
exports.DescribeEventConfigurationsCommand = DescribeEventConfigurationsCommand;
exports.DescribeFleetMetricCommand = DescribeFleetMetricCommand;
exports.DescribeIndexCommand = DescribeIndexCommand;
exports.DescribeJobCommand = DescribeJobCommand;
exports.DescribeJobExecutionCommand = DescribeJobExecutionCommand;
exports.DescribeJobTemplateCommand = DescribeJobTemplateCommand;
exports.DescribeManagedJobTemplateCommand = DescribeManagedJobTemplateCommand;
exports.DescribeMitigationActionCommand = DescribeMitigationActionCommand;
exports.DescribeProvisioningTemplateCommand = DescribeProvisioningTemplateCommand;
exports.DescribeProvisioningTemplateVersionCommand = DescribeProvisioningTemplateVersionCommand;
exports.DescribeRoleAliasCommand = DescribeRoleAliasCommand;
exports.DescribeScheduledAuditCommand = DescribeScheduledAuditCommand;
exports.DescribeSecurityProfileCommand = DescribeSecurityProfileCommand;
exports.DescribeStreamCommand = DescribeStreamCommand;
exports.DescribeThingCommand = DescribeThingCommand;
exports.DescribeThingGroupCommand = DescribeThingGroupCommand;
exports.DescribeThingRegistrationTaskCommand = DescribeThingRegistrationTaskCommand;
exports.DescribeThingTypeCommand = DescribeThingTypeCommand;
exports.DetachPolicyCommand = DetachPolicyCommand;
exports.DetachPrincipalPolicyCommand = DetachPrincipalPolicyCommand;
exports.DetachSecurityProfileCommand = DetachSecurityProfileCommand;
exports.DetachThingPrincipalCommand = DetachThingPrincipalCommand;
exports.DetectMitigationActionExecutionStatus = DetectMitigationActionExecutionStatus;
exports.DetectMitigationActionsTaskStatus = DetectMitigationActionsTaskStatus;
exports.DeviceCertificateUpdateAction = DeviceCertificateUpdateAction;
exports.DeviceDefenderIndexingMode = DeviceDefenderIndexingMode;
exports.DimensionType = DimensionType;
exports.DimensionValueOperator = DimensionValueOperator;
exports.DisableTopicRuleCommand = DisableTopicRuleCommand;
exports.DisassociateSbomFromPackageVersionCommand = DisassociateSbomFromPackageVersionCommand;
exports.DisconnectReasonValue = DisconnectReasonValue;
exports.DomainConfigurationStatus = DomainConfigurationStatus;
exports.DomainType = DomainType;
exports.DynamicGroupStatus = DynamicGroupStatus;
exports.DynamoKeyType = DynamoKeyType;
exports.EnableTopicRuleCommand = EnableTopicRuleCommand;
exports.EncryptionType = EncryptionType;
exports.EventType = EventType;
exports.FieldType = FieldType;
exports.FleetMetricUnit = FleetMetricUnit;
exports.GetBehaviorModelTrainingSummariesCommand = GetBehaviorModelTrainingSummariesCommand;
exports.GetBucketsAggregationCommand = GetBucketsAggregationCommand;
exports.GetCardinalityCommand = GetCardinalityCommand;
exports.GetCommandCommand = GetCommandCommand;
exports.GetCommandExecutionCommand = GetCommandExecutionCommand;
exports.GetEffectivePoliciesCommand = GetEffectivePoliciesCommand;
exports.GetIndexingConfigurationCommand = GetIndexingConfigurationCommand;
exports.GetJobDocumentCommand = GetJobDocumentCommand;
exports.GetLoggingOptionsCommand = GetLoggingOptionsCommand;
exports.GetOTAUpdateCommand = GetOTAUpdateCommand;
exports.GetPackageCommand = GetPackageCommand;
exports.GetPackageConfigurationCommand = GetPackageConfigurationCommand;
exports.GetPackageVersionCommand = GetPackageVersionCommand;
exports.GetPercentilesCommand = GetPercentilesCommand;
exports.GetPolicyCommand = GetPolicyCommand;
exports.GetPolicyVersionCommand = GetPolicyVersionCommand;
exports.GetRegistrationCodeCommand = GetRegistrationCodeCommand;
exports.GetStatisticsCommand = GetStatisticsCommand;
exports.GetThingConnectivityDataCommand = GetThingConnectivityDataCommand;
exports.GetTopicRuleCommand = GetTopicRuleCommand;
exports.GetTopicRuleDestinationCommand = GetTopicRuleDestinationCommand;
exports.GetV2LoggingOptionsCommand = GetV2LoggingOptionsCommand;
exports.IndexStatus = IndexStatus;
exports.IoT = IoT;
exports.IoTClient = IoTClient;
exports.JobEndBehavior = JobEndBehavior;
exports.JobExecutionFailureType = JobExecutionFailureType;
exports.JobExecutionStatus = JobExecutionStatus;
exports.JobStatus = JobStatus;
exports.ListActiveViolationsCommand = ListActiveViolationsCommand;
exports.ListAttachedPoliciesCommand = ListAttachedPoliciesCommand;
exports.ListAuditFindingsCommand = ListAuditFindingsCommand;
exports.ListAuditMitigationActionsExecutionsCommand = ListAuditMitigationActionsExecutionsCommand;
exports.ListAuditMitigationActionsTasksCommand = ListAuditMitigationActionsTasksCommand;
exports.ListAuditSuppressionsCommand = ListAuditSuppressionsCommand;
exports.ListAuditTasksCommand = ListAuditTasksCommand;
exports.ListAuthorizersCommand = ListAuthorizersCommand;
exports.ListBillingGroupsCommand = ListBillingGroupsCommand;
exports.ListCACertificatesCommand = ListCACertificatesCommand;
exports.ListCertificateProvidersCommand = ListCertificateProvidersCommand;
exports.ListCertificatesByCACommand = ListCertificatesByCACommand;
exports.ListCertificatesCommand = ListCertificatesCommand;
exports.ListCommandExecutionsCommand = ListCommandExecutionsCommand;
exports.ListCommandsCommand = ListCommandsCommand;
exports.ListCustomMetricsCommand = ListCustomMetricsCommand;
exports.ListDetectMitigationActionsExecutionsCommand = ListDetectMitigationActionsExecutionsCommand;
exports.ListDetectMitigationActionsTasksCommand = ListDetectMitigationActionsTasksCommand;
exports.ListDimensionsCommand = ListDimensionsCommand;
exports.ListDomainConfigurationsCommand = ListDomainConfigurationsCommand;
exports.ListFleetMetricsCommand = ListFleetMetricsCommand;
exports.ListIndicesCommand = ListIndicesCommand;
exports.ListJobExecutionsForJobCommand = ListJobExecutionsForJobCommand;
exports.ListJobExecutionsForThingCommand = ListJobExecutionsForThingCommand;
exports.ListJobTemplatesCommand = ListJobTemplatesCommand;
exports.ListJobsCommand = ListJobsCommand;
exports.ListManagedJobTemplatesCommand = ListManagedJobTemplatesCommand;
exports.ListMetricValuesCommand = ListMetricValuesCommand;
exports.ListMitigationActionsCommand = ListMitigationActionsCommand;
exports.ListOTAUpdatesCommand = ListOTAUpdatesCommand;
exports.ListOutgoingCertificatesCommand = ListOutgoingCertificatesCommand;
exports.ListPackageVersionsCommand = ListPackageVersionsCommand;
exports.ListPackagesCommand = ListPackagesCommand;
exports.ListPoliciesCommand = ListPoliciesCommand;
exports.ListPolicyPrincipalsCommand = ListPolicyPrincipalsCommand;
exports.ListPolicyVersionsCommand = ListPolicyVersionsCommand;
exports.ListPrincipalPoliciesCommand = ListPrincipalPoliciesCommand;
exports.ListPrincipalThingsCommand = ListPrincipalThingsCommand;
exports.ListPrincipalThingsV2Command = ListPrincipalThingsV2Command;
exports.ListProvisioningTemplateVersionsCommand = ListProvisioningTemplateVersionsCommand;
exports.ListProvisioningTemplatesCommand = ListProvisioningTemplatesCommand;
exports.ListRelatedResourcesForAuditFindingCommand = ListRelatedResourcesForAuditFindingCommand;
exports.ListRoleAliasesCommand = ListRoleAliasesCommand;
exports.ListSbomValidationResultsCommand = ListSbomValidationResultsCommand;
exports.ListScheduledAuditsCommand = ListScheduledAuditsCommand;
exports.ListSecurityProfilesCommand = ListSecurityProfilesCommand;
exports.ListSecurityProfilesForTargetCommand = ListSecurityProfilesForTargetCommand;
exports.ListStreamsCommand = ListStreamsCommand;
exports.ListTagsForResourceCommand = ListTagsForResourceCommand;
exports.ListTargetsForPolicyCommand = ListTargetsForPolicyCommand;
exports.ListTargetsForSecurityProfileCommand = ListTargetsForSecurityProfileCommand;
exports.ListThingGroupsCommand = ListThingGroupsCommand;
exports.ListThingGroupsForThingCommand = ListThingGroupsForThingCommand;
exports.ListThingPrincipalsCommand = ListThingPrincipalsCommand;
exports.ListThingPrincipalsV2Command = ListThingPrincipalsV2Command;
exports.ListThingRegistrationTaskReportsCommand = ListThingRegistrationTaskReportsCommand;
exports.ListThingRegistrationTasksCommand = ListThingRegistrationTasksCommand;
exports.ListThingTypesCommand = ListThingTypesCommand;
exports.ListThingsCommand = ListThingsCommand;
exports.ListThingsInBillingGroupCommand = ListThingsInBillingGroupCommand;
exports.ListThingsInThingGroupCommand = ListThingsInThingGroupCommand;
exports.ListTopicRuleDestinationsCommand = ListTopicRuleDestinationsCommand;
exports.ListTopicRulesCommand = ListTopicRulesCommand;
exports.ListV2LoggingLevelsCommand = ListV2LoggingLevelsCommand;
exports.ListViolationEventsCommand = ListViolationEventsCommand;
exports.LogLevel = LogLevel;
exports.LogTargetType = LogTargetType;
exports.MessageFormat = MessageFormat;
exports.MitigationActionType = MitigationActionType;
exports.ModelStatus = ModelStatus;
exports.NamedShadowIndexingMode = NamedShadowIndexingMode;
exports.OTAUpdateStatus = OTAUpdateStatus;
exports.OutputFormat = OutputFormat;
exports.PackageVersionAction = PackageVersionAction;
exports.PackageVersionStatus = PackageVersionStatus;
exports.PolicyTemplateName = PolicyTemplateName;
exports.Protocol = Protocol;
exports.PutVerificationStateOnViolationCommand = PutVerificationStateOnViolationCommand;
exports.RegisterCACertificateCommand = RegisterCACertificateCommand;
exports.RegisterCertificateCommand = RegisterCertificateCommand;
exports.RegisterCertificateWithoutCACommand = RegisterCertificateWithoutCACommand;
exports.RegisterThingCommand = RegisterThingCommand;
exports.RejectCertificateTransferCommand = RejectCertificateTransferCommand;
exports.RemoveThingFromBillingGroupCommand = RemoveThingFromBillingGroupCommand;
exports.RemoveThingFromThingGroupCommand = RemoveThingFromThingGroupCommand;
exports.ReplaceTopicRuleCommand = ReplaceTopicRuleCommand;
exports.ReportType = ReportType;
exports.ResourceType = ResourceType;
exports.RetryableFailureType = RetryableFailureType;
exports.SbomValidationErrorCode = SbomValidationErrorCode;
exports.SbomValidationResult = SbomValidationResult;
exports.SbomValidationStatus = SbomValidationStatus;
exports.SearchIndexCommand = SearchIndexCommand;
exports.ServerCertificateStatus = ServerCertificateStatus;
exports.ServiceType = ServiceType;
exports.SetDefaultAuthorizerCommand = SetDefaultAuthorizerCommand;
exports.SetDefaultPolicyVersionCommand = SetDefaultPolicyVersionCommand;
exports.SetLoggingOptionsCommand = SetLoggingOptionsCommand;
exports.SetV2LoggingLevelCommand = SetV2LoggingLevelCommand;
exports.SetV2LoggingOptionsCommand = SetV2LoggingOptionsCommand;
exports.SortOrder = SortOrder;
exports.StartAuditMitigationActionsTaskCommand = StartAuditMitigationActionsTaskCommand;
exports.StartDetectMitigationActionsTaskCommand = StartDetectMitigationActionsTaskCommand;
exports.StartOnDemandAuditTaskCommand = StartOnDemandAuditTaskCommand;
exports.StartThingRegistrationTaskCommand = StartThingRegistrationTaskCommand;
exports.Status = Status;
exports.StopThingRegistrationTaskCommand = StopThingRegistrationTaskCommand;
exports.TagResourceCommand = TagResourceCommand;
exports.TargetFieldOrder = TargetFieldOrder;
exports.TargetSelection = TargetSelection;
exports.TemplateType = TemplateType;
exports.TestAuthorizationCommand = TestAuthorizationCommand;
exports.TestInvokeAuthorizerCommand = TestInvokeAuthorizerCommand;
exports.ThingConnectivityIndexingMode = ThingConnectivityIndexingMode;
exports.ThingGroupIndexingMode = ThingGroupIndexingMode;
exports.ThingIndexingMode = ThingIndexingMode;
exports.ThingPrincipalType = ThingPrincipalType;
exports.TopicRuleDestinationStatus = TopicRuleDestinationStatus;
exports.TransferCertificateCommand = TransferCertificateCommand;
exports.UntagResourceCommand = UntagResourceCommand;
exports.UpdateAccountAuditConfigurationCommand = UpdateAccountAuditConfigurationCommand;
exports.UpdateAuditSuppressionCommand = UpdateAuditSuppressionCommand;
exports.UpdateAuthorizerCommand = UpdateAuthorizerCommand;
exports.UpdateBillingGroupCommand = UpdateBillingGroupCommand;
exports.UpdateCACertificateCommand = UpdateCACertificateCommand;
exports.UpdateCertificateCommand = UpdateCertificateCommand;
exports.UpdateCertificateProviderCommand = UpdateCertificateProviderCommand;
exports.UpdateCommandCommand = UpdateCommandCommand;
exports.UpdateCustomMetricCommand = UpdateCustomMetricCommand;
exports.UpdateDimensionCommand = UpdateDimensionCommand;
exports.UpdateDomainConfigurationCommand = UpdateDomainConfigurationCommand;
exports.UpdateDynamicThingGroupCommand = UpdateDynamicThingGroupCommand;
exports.UpdateEncryptionConfigurationCommand = UpdateEncryptionConfigurationCommand;
exports.UpdateEventConfigurationsCommand = UpdateEventConfigurationsCommand;
exports.UpdateFleetMetricCommand = UpdateFleetMetricCommand;
exports.UpdateIndexingConfigurationCommand = UpdateIndexingConfigurationCommand;
exports.UpdateJobCommand = UpdateJobCommand;
exports.UpdateMitigationActionCommand = UpdateMitigationActionCommand;
exports.UpdatePackageCommand = UpdatePackageCommand;
exports.UpdatePackageConfigurationCommand = UpdatePackageConfigurationCommand;
exports.UpdatePackageVersionCommand = UpdatePackageVersionCommand;
exports.UpdateProvisioningTemplateCommand = UpdateProvisioningTemplateCommand;
exports.UpdateRoleAliasCommand = UpdateRoleAliasCommand;
exports.UpdateScheduledAuditCommand = UpdateScheduledAuditCommand;
exports.UpdateSecurityProfileCommand = UpdateSecurityProfileCommand;
exports.UpdateStreamCommand = UpdateStreamCommand;
exports.UpdateThingCommand = UpdateThingCommand;
exports.UpdateThingGroupCommand = UpdateThingGroupCommand;
exports.UpdateThingGroupsForThingCommand = UpdateThingGroupsForThingCommand;
exports.UpdateThingTypeCommand = UpdateThingTypeCommand;
exports.UpdateTopicRuleDestinationCommand = UpdateTopicRuleDestinationCommand;
exports.ValidateSecurityProfileBehaviorsCommand = ValidateSecurityProfileBehaviorsCommand;
exports.VerificationState = VerificationState;
exports.ViolationEventType = ViolationEventType;
exports.paginateGetBehaviorModelTrainingSummaries = paginateGetBehaviorModelTrainingSummaries;
exports.paginateListActiveViolations = paginateListActiveViolations;
exports.paginateListAttachedPolicies = paginateListAttachedPolicies;
exports.paginateListAuditFindings = paginateListAuditFindings;
exports.paginateListAuditMitigationActionsExecutions = paginateListAuditMitigationActionsExecutions;
exports.paginateListAuditMitigationActionsTasks = paginateListAuditMitigationActionsTasks;
exports.paginateListAuditSuppressions = paginateListAuditSuppressions;
exports.paginateListAuditTasks = paginateListAuditTasks;
exports.paginateListAuthorizers = paginateListAuthorizers;
exports.paginateListBillingGroups = paginateListBillingGroups;
exports.paginateListCACertificates = paginateListCACertificates;
exports.paginateListCertificates = paginateListCertificates;
exports.paginateListCertificatesByCA = paginateListCertificatesByCA;
exports.paginateListCommandExecutions = paginateListCommandExecutions;
exports.paginateListCommands = paginateListCommands;
exports.paginateListCustomMetrics = paginateListCustomMetrics;
exports.paginateListDetectMitigationActionsExecutions = paginateListDetectMitigationActionsExecutions;
exports.paginateListDetectMitigationActionsTasks = paginateListDetectMitigationActionsTasks;
exports.paginateListDimensions = paginateListDimensions;
exports.paginateListDomainConfigurations = paginateListDomainConfigurations;
exports.paginateListFleetMetrics = paginateListFleetMetrics;
exports.paginateListIndices = paginateListIndices;
exports.paginateListJobExecutionsForJob = paginateListJobExecutionsForJob;
exports.paginateListJobExecutionsForThing = paginateListJobExecutionsForThing;
exports.paginateListJobTemplates = paginateListJobTemplates;
exports.paginateListJobs = paginateListJobs;
exports.paginateListManagedJobTemplates = paginateListManagedJobTemplates;
exports.paginateListMetricValues = paginateListMetricValues;
exports.paginateListMitigationActions = paginateListMitigationActions;
exports.paginateListOTAUpdates = paginateListOTAUpdates;
exports.paginateListOutgoingCertificates = paginateListOutgoingCertificates;
exports.paginateListPackageVersions = paginateListPackageVersions;
exports.paginateListPackages = paginateListPackages;
exports.paginateListPolicies = paginateListPolicies;
exports.paginateListPolicyPrincipals = paginateListPolicyPrincipals;
exports.paginateListPrincipalPolicies = paginateListPrincipalPolicies;
exports.paginateListPrincipalThings = paginateListPrincipalThings;
exports.paginateListPrincipalThingsV2 = paginateListPrincipalThingsV2;
exports.paginateListProvisioningTemplateVersions = paginateListProvisioningTemplateVersions;
exports.paginateListProvisioningTemplates = paginateListProvisioningTemplates;
exports.paginateListRelatedResourcesForAuditFinding = paginateListRelatedResourcesForAuditFinding;
exports.paginateListRoleAliases = paginateListRoleAliases;
exports.paginateListSbomValidationResults = paginateListSbomValidationResults;
exports.paginateListScheduledAudits = paginateListScheduledAudits;
exports.paginateListSecurityProfiles = paginateListSecurityProfiles;
exports.paginateListSecurityProfilesForTarget = paginateListSecurityProfilesForTarget;
exports.paginateListStreams = paginateListStreams;
exports.paginateListTagsForResource = paginateListTagsForResource;
exports.paginateListTargetsForPolicy = paginateListTargetsForPolicy;
exports.paginateListTargetsForSecurityProfile = paginateListTargetsForSecurityProfile;
exports.paginateListThingGroups = paginateListThingGroups;
exports.paginateListThingGroupsForThing = paginateListThingGroupsForThing;
exports.paginateListThingPrincipals = paginateListThingPrincipals;
exports.paginateListThingPrincipalsV2 = paginateListThingPrincipalsV2;
exports.paginateListThingRegistrationTaskReports = paginateListThingRegistrationTaskReports;
exports.paginateListThingRegistrationTasks = paginateListThingRegistrationTasks;
exports.paginateListThingTypes = paginateListThingTypes;
exports.paginateListThings = paginateListThings;
exports.paginateListThingsInBillingGroup = paginateListThingsInBillingGroup;
exports.paginateListThingsInThingGroup = paginateListThingsInThingGroup;
exports.paginateListTopicRuleDestinations = paginateListTopicRuleDestinations;
exports.paginateListTopicRules = paginateListTopicRules;
exports.paginateListV2LoggingLevels = paginateListV2LoggingLevels;
exports.paginateListViolationEvents = paginateListViolationEvents;
Object.prototype.hasOwnProperty.call(schemas_0, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: schemas_0['__proto__']
    });

Object.keys(schemas_0).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = schemas_0[k];
});
Object.prototype.hasOwnProperty.call(errors, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: errors['__proto__']
    });

Object.keys(errors).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = errors[k];
});
